import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, TextField, Card, CardContent, Autocomplete,
  Tooltip, CircularProgress, Alert, Select, MenuItem, InputLabel, FormControl,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tabs, Tab,
  Switch, FormControlLabel, Checkbox, ListItemText, Collapse
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PaymentIcon from '@mui/icons-material/Payment';
import ComprobanteBlock, { useComprobanteUploader } from './common/ComprobanteBlock';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import LockIcon from '@mui/icons-material/Lock';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import UndoIcon from '@mui/icons-material/Undo';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { StyledDialog, inputStyles } from './common';
import { fmtFechaHora } from '../utils/fechaUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

// Consulta pura para el rol comercial — ver arriba en AdminCxC.
function esSoloConsulta() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase() === 'comercial'; }
  catch { return false; }
}

function getEmpresaId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return localStorage.getItem('empresa_activa') || user.company_id || user.empresa_id || '1';
  } catch { return '1'; }
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

/** $760.000 -> "$760k" · $2.343.000 -> "$2,34M". Mismo formato que los tiles
 *  de Venta diaria, para que la cifra quepa siempre en una línea. */
function _fmtCompacto(n) {
  const v = Number(n || 0);
  if (v === 0) return '$0';
  const abs = Math.abs(v), sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    const t = m >= 100 ? m.toFixed(0) : m.toFixed(2).replace(/\.?0+$/, '');
    return `${sign}$${t.replace('.', ',')}M`;
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    const t = k >= 100 ? k.toFixed(0) : k.toFixed(1).replace(/\.?0+$/, '');
    return `${sign}$${t.replace('.', ',')}k`;
  }
  return `${sign}$${abs}`;
}

/** Fecha corta para los tooltips: 26/08/2026. */
function fmtFecha(iso) {
  if (!iso) return '';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

function fmtMoney(n) {
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n || 0));
}

function firstDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

const gradientComision = 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)';
const shadowComision = 'rgba(20, 184, 166, 0.4)';

const cardStyle = {
  borderRadius: 4,
  background: 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  overflow: 'hidden',
};


export default function AdminComisiones() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: '#134e4a', fontWeight: 700, fontSize: 20, mb: 0.5 }}>
          Finanzas
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 13 }}>
          Cuentas por cobrar, cuentas por pagar, comisiones y liquidaciones.
        </Typography>
      </Box>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="Cartera Turistas" />
        <Tab icon={<AccountBalanceIcon />} iconPosition="start" label="Deuda Agencias" />
        <Tab icon={<PaymentIcon />} iconPosition="start" label="Vouchers" />
        <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Comisiones" />
        <Tab icon={<CompareArrowsIcon />} iconPosition="start" label="Cruce" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Liquidaciones" />
        <Tab icon={<StorefrontIcon />} iconPosition="start" label="Ventas Directas" />
      </Tabs>
      {tab === 0 && <CarteraTuristasTab />}
      {tab === 1 && <DeudaTab />}
      {tab === 2 && <VouchersTab />}
      {tab === 3 && <DashboardTab />}
      {tab === 4 && <CruceTab />}
      {tab === 5 && <HistorialTab />}
      {tab === 6 && <VentasDirectasTab />}
    </Box>
  );
}


// ============================================================================
// TAB: CARTERA TURISTAS
// ============================================================================
export function CarteraTuristasTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonth());
  const [fechaHasta, setFechaHasta] = useState(today());
  const [soloPendientes, setSoloPendientes] = useState(true);
  const empresaId = getEmpresaId();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, solo_pendientes: String(soloPendientes) });
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      const res = await fetch(`${API_BASE}/turismo/deuda/cartera-turistas?${params}`, { headers: getAuthHeaders() });
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta, soloPendientes]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleMarcarPagado = async (id) => {
    if (!window.confirm('¿Marcar este saldo como cobrado?')) return;
    const res = await fetch(`${API_BASE}/turismo/deuda/cartera-turistas/${id}/pagar`, { method: 'PATCH', headers: getAuthHeaders() });
    if (res.ok) cargar();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField sx={{ ...inputStyles, minWidth: 160 }} type="date" label="Desde"
          InputLabelProps={{ shrink: true }} value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
        <TextField sx={{ ...inputStyles, minWidth: 160 }} type="date" label="Hasta"
          InputLabelProps={{ shrink: true }} value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
        <Button variant={soloPendientes ? 'contained' : 'outlined'} size="small"
          onClick={() => setSoloPendientes(!soloPendientes)}
          sx={{ textTransform: 'none', borderRadius: 2 }}>
          {soloPendientes ? 'Solo pendientes' : 'Todos'}
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard title="Total por cobrar" value={fmtMoney(data?.total_por_cobrar)} icon={<ReceiptLongIcon />} color="#dc2626" />
        <KpiCard title="Turistas con saldo" value={String(data?.total_turistas || 0)} icon={<PendingIcon />} color="#f59e0b" />
        <KpiCard title="Vía agencia" value={String(data?.via_agencia || 0)} icon={<BusinessIcon />} color="#6366f1" />
        <KpiCard title="Directos" value={String(data?.via_directo || 0)} icon={<WhatsAppIcon />} color="#25D366" />
      </Box>

      {(!data?.turistas || data.turistas.length === 0) ? (
        <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: '#10b981', mb: 1.5 }} />
          <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>
            {soloPendientes ? 'No hay saldos pendientes' : 'No hay reservas en este período'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={cardStyle}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Turista</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Tour</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Canal</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Saldo pendiente</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.turistas.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{t.nombre_cliente}</Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>{t.codigo_reserva}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{t.paquete_nombre}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{t.fecha_tour}</TableCell>
                  <TableCell>
                    <Chip size="small" label={t.canal}
                      sx={{ fontSize: 10, height: 20, bgcolor: t.agencia_nombre ? '#ede9fe' : '#ecfdf5', color: t.agencia_nombre ? '#6d28d9' : '#059669' }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmtMoney(t.precio_total)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: t.saldo_pendiente > 0 ? '#dc2626' : '#10b981' }}>
                    {t.saldo_pendiente > 0 ? fmtMoney(t.saldo_pendiente) : 'Pagado'}
                  </TableCell>
                  <TableCell align="center">
                    {t.saldo_pendiente > 0 && (
                      <Tooltip title="Marcar como cobrado">
                        <IconButton size="small" onClick={() => handleMarcarPagado(t.id)} sx={{ color: '#10b981' }}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}


// ============================================================================
// TAB: VOUCHERS DE PAGO
// ============================================================================
export function VouchersTab() {
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const empresaId = getEmpresaId();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId });
      if (filtroEstado) params.append('estado', filtroEstado);
      const [vRes, sRes] = await Promise.all([
        fetch(`${API_BASE}/turismo/vouchers?${params}`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/vouchers/stats?empresa_id=${empresaId}`, { headers: getAuthHeaders() }),
      ]);
      setVouchers(await vRes.json());
      setStats(await sRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleVerificar = async (id, estado) => {
    const notas = estado === 'rechazado' ? window.prompt('Motivo del rechazo:') : null;
    if (estado === 'rechazado' && notas === null) return;
    const userId = parseInt(localStorage.getItem('user_id')) || null;
    const res = await fetch(`${API_BASE}/turismo/vouchers/${id}/verificar`, {
      method: 'PATCH', headers: getAuthHeaders(),
      body: JSON.stringify({ estado, notas_revision: notas, user_id: userId }),
    });
    if (res.ok) cargar();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard title="Pendientes" value={String(stats.pendientes || 0)} icon={<PendingIcon />} color="#f59e0b" />
        <KpiCard title="Monto pendiente" value={fmtMoney(stats.monto_pendiente)} icon={<PaymentIcon />} color="#dc2626" />
        <KpiCard title="Aprobados" value={String(stats.aprobados || 0)} icon={<CheckCircleIcon />} color="#10b981" />
        <KpiCard title="Monto aprobado" value={fmtMoney(stats.monto_aprobado)} icon={<AccountBalanceWalletIcon />} color="#0891b2" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {['pendiente', 'aprobado', 'rechazado', ''].map(e => (
          <Button key={e || 'all'} size="small"
            variant={filtroEstado === e ? 'contained' : 'outlined'}
            onClick={() => setFiltroEstado(e)}
            sx={{ textTransform: 'none', borderRadius: 2 }}>
            {e === '' ? 'Todos' : e === 'pendiente' ? 'Pendientes' : e === 'aprobado' ? 'Aprobados' : 'Rechazados'}
          </Button>
        ))}
      </Box>

      {vouchers.length === 0 ? (
        <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
          <PaymentIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 1.5 }} />
          <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>
            No hay vouchers {filtroEstado ? filtroEstado + 's' : ''}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {vouchers.map(v => {
            const estColor = v.estado === 'aprobado' ? '#10b981' : v.estado === 'rechazado' ? '#ef4444' : '#f59e0b';
            return (
              <Box key={v.id} sx={{ display: 'flex', gap: 2, p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.9)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: `1px solid ${estColor}22`, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Imagen */}
                {v.imagen_url && v.imagen_url !== 'pendiente' && (
                  <Box sx={{ width: 80, height: 80, borderRadius: 2, overflow: 'hidden', flexShrink: 0, cursor: 'pointer',
                    border: '1px solid #e5e7eb' }}
                    onClick={() => window.open(`${API_BASE}${v.imagen_url}`, '_blank')}>
                    <img src={`${API_BASE}${v.imagen_url}`} alt="voucher" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                )}
                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#134e4a' }}>
                      {v.monto ? fmtMoney(v.monto) : (v.reserva_precio_total ? fmtMoney(v.reserva_precio_total) : 'Sin monto')}
                    </Typography>
                    <Chip size="small" label={v.estado} sx={{ fontSize: 10, height: 20, bgcolor: estColor + '22', color: estColor, fontWeight: 600 }} />
                    {v.reserva_estado === 'hold' && (
                      <Chip size="small" label="HOLD" sx={{ fontSize: 10, height: 20, bgcolor: '#eab30822', color: '#a16207', fontWeight: 700 }} />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: '#475569' }}>
                    Agencia: <strong>{v.agencia_nombre}</strong>
                  </Typography>
                  {v.codigo_reserva ? (
                    <Box sx={{ mt: 0.5, p: 1, bgcolor: '#f0f9ff', borderRadius: 1.5, border: '1px solid #bae6fd' }}>
                      <Typography sx={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>
                        📋 {v.codigo_reserva} — {v.nombre_cliente}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#075985' }}>
                        {v.reserva_paquete_nombre && <>🌴 {v.reserva_paquete_nombre}</>}
                        {v.reserva_destino_nombre && <> · {v.reserva_destino_nombre}</>}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#075985' }}>
                        {v.reserva_fecha_salida && <>📅 {v.reserva_fecha_salida.slice(0, 10)}</>}
                        {v.reserva_total_pax && <> · 👥 {v.reserva_total_pax} pax</>}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 11, color: '#f59e0b', fontStyle: 'italic', mt: 0.3 }}>
                      ⚠️ Sin reserva vinculada — verificar manualmente
                    </Typography>
                  )}
                  {v.referencia && <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.3 }}>Ref: {v.referencia}</Typography>}
                  <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Recibido: {fmtFechaHora(v.created_at)}</Typography>
                  {v.notas_revision && <Typography sx={{ fontSize: 11, color: '#ef4444', mt: 0.5 }}>Nota: {v.notas_revision}</Typography>}
                </Box>
                {/* Acciones */}
                {v.estado === 'pendiente' && !esSoloConsulta() && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" variant="contained" color="success"
                      onClick={() => handleVerificar(v.id, 'aprobado')}
                      sx={{ textTransform: 'none', fontSize: 12 }}>
                      Aprobar
                    </Button>
                    <Button size="small" variant="outlined" color="error"
                      onClick={() => handleVerificar(v.id, 'rechazado')}
                      sx={{ textTransform: 'none', fontSize: 12 }}>
                      Rechazar
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}


// ============================================================================
// TAB: DEUDA DE AGENCIAS
// ============================================================================
export function DeudaTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonth());
  const [fechaHasta, setFechaHasta] = useState(today());
  const [detalleAgId, setDetalleAgId] = useState(null);
  const empresaId = getEmpresaId();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId });
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      const res = await fetch(`${API_BASE}/turismo/deuda/resumen?${params}`, { headers: getAuthHeaders() });
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField sx={{ ...inputStyles, minWidth: 160 }} type="date" label="Desde"
          InputLabelProps={{ shrink: true }} value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
        <TextField sx={{ ...inputStyles, minWidth: 160 }} type="date" label="Hasta"
          InputLabelProps={{ shrink: true }} value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard title="Deuda Total" value={fmtMoney(data?.total_deuda_global)} icon={<AccountBalanceIcon />} color="#dc2626" />
        <KpiCard title="Agencias con deuda" value={String(data?.agencias?.length || 0)} icon={<BusinessIcon />} color="#6366f1" />
      </Box>

      {(!data?.agencias || data.agencias.length === 0) ? (
        <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: '#10b981', mb: 1.5 }} />
          <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>Sin deuda pendiente</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={cardStyle}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Agencia</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center"># Reservas</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Total tours</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Turista paga muelle</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Voucher aprobado</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Deuda agencia</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.agencias.map(a => (
                <TableRow key={a.agencia_id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{a.agencia_nombre}</Typography>
                  </TableCell>
                  <TableCell align="center"><Chip label={a.num_reservas} size="small" color="primary" /></TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>{fmtMoney(a.total_precio)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, color: '#64748b' }}>{fmtMoney(a.total_saldo_turista)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, color: '#0891b2' }}>{fmtMoney(a.total_vouchers_aprobados)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: a.total_deuda > 0 ? '#dc2626' : '#15803d' }}>{fmtMoney(a.total_deuda)}</TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="outlined" onClick={() => setDetalleAgId(a.agencia_id)} sx={{ textTransform: 'none' }}>
                      Ver detalle
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {detalleAgId && (
        <DetalleDeudaDialog agenciaId={detalleAgId} fechaDesde={fechaDesde} fechaHasta={fechaHasta}
          onClose={() => setDetalleAgId(null)} />
      )}
    </Box>
  );
}


function DetalleDeudaDialog({ agenciaId, fechaDesde, fechaHasta, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (fechaDesde) params.append('fecha_desde', fechaDesde);
        if (fechaHasta) params.append('fecha_hasta', fechaHasta);
        const res = await fetch(`${API_BASE}/turismo/deuda/agencia/${agenciaId}?${params}`, { headers: getAuthHeaders() });
        setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [agenciaId, fechaDesde, fechaHasta]);

  const totales = data?.totales || {};
  const bkr = data?.breakdown_reservas || {};
  const pgd = data?.pagos_desglose || {};
  const cmd = data?.comisiones_desglose || {};

  // Bloque de KPI reutilizable — etiqueta arriba, valor abajo, con operador (+/-/=)
  const KpiTile = ({ label, value, color = '#1e293b', bg = '#f8fafc', border = '#e2e8f0', large = false, bold = false }) => (
    <Box sx={{
      px: 1.5, py: 1, borderRadius: 1.5, bgcolor: bg, border: `1px solid ${border}`,
      minWidth: large ? 150 : 130, flex: '0 0 auto', textAlign: 'center',
    }}>
      <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: large ? 17 : 14, color, fontWeight: bold ? 800 : 700, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
  const OpTile = ({ op }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5 }}>
      <Typography sx={{ fontSize: 22, color: '#94a3b8', fontWeight: 700 }}>{op}</Typography>
    </Box>
  );

  return (
    <StyledDialog open onClose={onClose}
      title={`Deuda — ${data?.agencia?.nombre || '...'}`}
      icon={<AccountBalanceIcon />} variant="compact" maxWidth="lg">
      <StyledDialog.Body compact>
        {loading ? <CircularProgress /> : (
          <>
            {/* ───────────────── Fila 1: Cálculo de la deuda ─────────────────
                Muestra la aritmética clara:
                Reservas · PVP total − Vouchers OK − Pagos aplicados = DEUDA REAL
                Y debajo el desglose de pagos por origen. */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', mb: 0.75 }}>
              Cálculo de la deuda
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'stretch', mb: 1 }}>
              <KpiTile
                label="Reservas"
                value={
                  <Box>
                    {totales.total_reservas || 0}
                    <Typography component="span" sx={{ display: 'block', fontSize: 9.5, color: '#64748b', fontWeight: 500, mt: 0.25 }}>
                      {(bkr.futuras || 0)} futuras · {(bkr.hoy || 0)} hoy · {(bkr.pasadas || 0)} pasadas
                    </Typography>
                  </Box>
                }
                bg="#f0f9ff" border="#bae6fd" color="#0369a1"
              />
              <KpiTile label="PVP total" value={fmtMoney(totales.total_precio)} bg="#e0f2fe" border="#7dd3fc" color="#0369a1" />
              <OpTile op="−" />
              <KpiTile label="Vouchers OK" value={fmtMoney(totales.total_vouchers_aprobados)} bg="#ecfeff" border="#a5f3fc" color="#0891b2" />
              <OpTile op="−" />
              <KpiTile label="Pagos aplicados" value={fmtMoney(totales.total_pagos_aplicados)} bg="#f0fdf4" border="#86efac" color="#15803d" />
              <OpTile op="=" />
              <KpiTile
                label="Deuda real"
                value={fmtMoney(totales.total_deuda_agencia)}
                bg={totales.total_deuda_agencia > 0 ? '#fef2f2' : '#f0fdf4'}
                border={totales.total_deuda_agencia > 0 ? '#fca5a5' : '#86efac'}
                color={totales.total_deuda_agencia > 0 ? '#dc2626' : '#15803d'}
                large bold
              />
            </Box>

            {/* Desglose de los "Pagos aplicados" — para hacer visible de dónde viene la plata */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5, pl: 0.5 }}>
              <Typography sx={{ fontSize: 10.5, color: '#64748b', alignSelf: 'center', mr: 0.5 }}>
                Desglose pagos:
              </Typography>
              <Chip size="small" label={`Comisión retenida agencia: ${fmtMoney(pgd.comision_retenida)}`}
                sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontSize: 10.5, height: 22 }} />
              <Chip size="small" label={`Pagos agencia → Entretur: ${fmtMoney(pgd.pagos_agencia_a_entretur)}`}
                sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontSize: 10.5, height: 22 }} />
              <Chip size="small" label={`Cobros muelle (turista): ${fmtMoney(pgd.cobros_muelle)}`}
                sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontSize: 10.5, height: 22 }} />
              {pgd.pagos_directos_cliente > 0 && (
                <Chip size="small" label={`Pago directo cliente: ${fmtMoney(pgd.pagos_directos_cliente)}`}
                  sx={{ bgcolor: '#fef3c7', color: '#a16207', fontSize: 10.5, height: 22 }} />
              )}
              {pgd.descuentos > 0 && (
                <Chip size="small" label={`Descuentos: ${fmtMoney(pgd.descuentos)}`}
                  sx={{ bgcolor: '#fce7f3', color: '#be185d', fontSize: 10.5, height: 22 }} />
              )}
            </Box>

            {/* ───────────────── Fila 2: Comisiones ─────────────────
                Muestra las 4 facetas: generadas · anticipadas · liquidadas · pendientes */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', mb: 0.75, mt: 1 }}>
              Comisiones de la agencia
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              <KpiTile label="Generadas" value={fmtMoney(cmd.generadas)}
                bg="#fefce8" border="#fde047" color="#a16207" />
              <KpiTile label="Anticipadas (retuvo agencia)" value={fmtMoney(cmd.anticipadas)}
                bg="#ede9fe" border="#c4b5fd" color="#6d28d9" />
              <KpiTile label="Liquidadas por Entretur" value={fmtMoney(cmd.liquidadas)}
                bg="#dcfce7" border="#86efac" color="#15803d" />
              <KpiTile label="Pendientes por pagar"
                value={fmtMoney(cmd.pendientes)}
                bg={cmd.pendientes > 0 ? '#fef2f2' : '#f0fdf4'}
                border={cmd.pendientes > 0 ? '#fca5a5' : '#86efac'}
                color={cmd.pendientes > 0 ? '#dc2626' : '#15803d'}
                bold
              />
            </Box>

            {/* Tabla detalle */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11 } }}>
                    <TableCell>Código</TableCell>
                    <TableCell>Tour</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Titular</TableCell>
                    <TableCell align="center">PAX</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Turista muelle</TableCell>
                    <TableCell align="right">Voucher aprob.</TableCell>
                    <TableCell align="right">Deuda agencia</TableCell>
                    <TableCell align="right">Comisión</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.reservas || []).map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontSize: 11 }}>
                        {r.codigo_reserva}
                        {r.tiquete_agencia && <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>Tiq: {r.tiquete_agencia}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{r.paquete_nombre}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{r.fecha_tour}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        {r.nombre_cliente}
                        {r.hotel && <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>🏨 {r.hotel}</Typography>}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: 11 }}>{r.adultos + r.ninos}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11 }}>{fmtMoney(r.precio_total)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, color: '#15803d' }}>{fmtMoney(r.saldo_turista)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, color: '#0891b2' }}>{fmtMoney(r.voucher_aprobado)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: r.deuda_agencia > 0 ? '#dc2626' : '#15803d' }}>{fmtMoney(r.deuda_agencia)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, color: '#a16207' }}>{fmtMoney(r.comision_valor)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.PrimaryButton fullWidth onClick={onClose}>Cerrar</StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


// ============================================================================
// TAB: COMISIONES — DASHBOARD POR AGENCIA
// ============================================================================
export function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // "Comisiones por pagar" es una vista de estado ACTUAL, no de período:
  // muestra cuánto se debe hoy, sin filtros de fecha. La lista de "Comisiones
  // pagadas" (HistorialTab) sí conserva el rango porque ahí importa el histórico.
  // Filtro por agencia — 'todas' = sin filtro; cualquier otro valor es agencia_id
  const [agenciaFiltro, setAgenciaFiltro] = useState('todas');
  // Tipo de pendiente: 'ambas' (default), 'exigibles', 'futuras'.
  const [tipoPendiente, setTipoPendiente] = useState('ambas');
  // Excluir agencias internas de Entretur (ENTRETUR, ENTRETUR PAGINA WEB).
  // Default true — al operador le interesa cobrar a terceros, no a sí mismo.
  const [excluirInternas, setExcluirInternas] = useState(true);
  // Por defecto ocultar agencias al día: el módulo es de cuentas por pagar,
  // no de reporte analítico. Toggle para mostrar todas si el operador lo quiere.
  const [soloConPendiente, setSoloConPendiente] = useState(true);
  const [agenciasList, setAgenciasList] = useState([]);
  const [detalleAgencia, setDetalleAgencia] = useState(null);
  const empresaId = getEmpresaId();

  // Cargar lista de agencias una sola vez (para poblar el selector)
  useEffect(() => {
    fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const arr = Array.isArray(list) ? list : (list?.agencias || []);
        // Ordenar alfabéticamente para el dropdown
        setAgenciasList([...arr].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
      })
      .catch(() => setAgenciasList([]));
  }, [empresaId]);

  // Construye los query params comunes al dashboard y a los exports.
  const buildParams = useCallback(() => {
    const p = new URLSearchParams({ empresa_id: empresaId });
    if (agenciaFiltro && agenciaFiltro !== 'todas') p.append('agencia_id', agenciaFiltro);
    if (tipoPendiente && tipoPendiente !== 'ambas') p.append('tipo_pendiente', tipoPendiente);
    p.append('excluir_internas', excluirInternas ? 'true' : 'false');
    return p;
  }, [empresaId, agenciaFiltro, tipoPendiente, excluirInternas]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/comisiones/dashboard?${buildParams()}`, { headers: getAuthHeaders() });
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [buildParams]);

  const descargar = (formato) => {
    // formato = 'pdf' | 'excel'. Abre el archivo en nueva pestaña —
    // el navegador dispara la descarga por el header Content-Disposition.
    const url = `${API_BASE}/turismo/comisiones/dashboard/export/${formato}?${buildParams()}`;
    window.open(url, '_blank');
  };

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

  const resumen = data?.resumen_global || {};

  return (
    <Box>
      {/* Filtros — agencia, tipo pendiente, exclusión internas + exports.
          Sin fechas: la vista muestra el estado actual de comisiones por pagar. */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Buscador de agencias en vez de un desplegable: con casi 200
            agencias, bajar por la lista era inviable. Escribiendo "bria"
            quedan solo las que empiezan así. */}
        <Autocomplete
          size="small"
          sx={{ ...inputStyles, minWidth: 260 }}
          options={agenciasList}
          getOptionLabel={(o) => o?.nombre || ''}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={agenciaFiltro === 'todas'
            ? null
            : (agenciasList.find(a => String(a.id) === String(agenciaFiltro)) || null)}
          onChange={(_, opcion) => setAgenciaFiltro(opcion ? opcion.id : 'todas')}
          // Prioriza las que EMPIEZAN por lo escrito, pero no descarta las que
          // lo contienen: quien busca "brahiam" no debería quedarse sin ver
          // "Agencia Brahiam" solo por no acertar la primera palabra.
          filterOptions={(opciones, { inputValue }) => {
            const q = (inputValue || '').trim().toLowerCase();
            if (!q) return opciones;
            const empiezan = [], contienen = [];
            for (const o of opciones) {
              const n = (o.nombre || '').toLowerCase();
              if (n.startsWith(q)) empiezan.push(o);
              else if (n.includes(q)) contienen.push(o);
            }
            return [...empiezan, ...contienen];
          }}
          noOptionsText="Ninguna agencia coincide"
          clearOnEscape
          renderInput={(params) => (
            <TextField {...params} label="Agencia" placeholder="Todas — escribe para buscar" />
          )}
        />
        <FormControl sx={{ ...inputStyles, minWidth: 190 }} size="small">
          <InputLabel>Vista</InputLabel>
          <Select value={tipoPendiente} label="Vista"
            onChange={e => setTipoPendiente(e.target.value)}>
            <MenuItem value="ambas">Exigibles + Futuras</MenuItem>
            <MenuItem value="exigibles">Solo exigibles hoy</MenuItem>
            <MenuItem value="futuras">Solo futuras (bloqueadas)</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={excluirInternas} onChange={e => setExcluirInternas(e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: 12, color: '#64748b' }}>Excluir Entretur (interno)</Typography>}
        />
        <FormControlLabel
          control={<Switch checked={!soloConPendiente} onChange={e => setSoloConPendiente(!e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: 12, color: '#64748b' }}>Ver también agencias al día</Typography>}
        />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
            onClick={() => descargar('pdf')}
            sx={{ textTransform: 'none', borderColor: '#dc2626', color: '#dc2626',
                  '&:hover': { borderColor: '#991b1b', bgcolor: '#fef2f2' } }}>
            PDF
          </Button>
          <Button size="small" variant="outlined" startIcon={<GridOnIcon />}
            onClick={() => descargar('excel')}
            sx={{ textTransform: 'none', borderColor: '#166534', color: '#166534',
                  '&:hover': { borderColor: '#14532d', bgcolor: '#f0fdf4' } }}>
            Excel
          </Button>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 11, color: '#64748b', fontStyle: 'italic', mb: 2 }}>
        🔒 Las comisiones de servicios aún no prestados aparecen bloqueadas
      </Typography>

      {/* KPIs — solo los relevantes al filtro actual (no mezclar sumas parciales).
            · Ambas → los 3 KPIs
            · Solo exigibles → solo "Exigible hoy" (Futuras=0 en este scope)
            · Solo futuras   → solo "Futuras (bloqueadas)" */}
      {(() => {
        const kpis = [];
        if (tipoPendiente === 'exigibles') {
          kpis.push({ label: 'Exigible hoy', v: resumen.total_pendiente_exigible, color: '#b91c1c', bg: '#fee2e2' });
        } else if (tipoPendiente === 'futuras') {
          kpis.push({ label: 'Futuras (bloqueadas)', v: resumen.total_pendiente_futura, color: '#475569', bg: '#f1f5f9' });
        } else {
          kpis.push({ label: 'Exigible hoy', v: resumen.total_pendiente_exigible, color: '#b91c1c', bg: '#fee2e2' });
          kpis.push({ label: 'Futuras (bloqueadas)', v: resumen.total_pendiente_futura, color: '#475569', bg: '#f1f5f9' });
          kpis.push({ label: 'Total pendiente', v: resumen.total_pendiente, color: '#0d9488', bg: '#ccfbf1' });
        }
        // Grid dinámico según cantidad de KPIs — no dejamos huecos raros
        const cols = kpis.length === 1 ? '1fr'
                   : kpis.length === 2 ? { xs: 'repeat(2,1fr)', sm: 'repeat(2, 1fr)' }
                   : { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(3, minmax(0,1fr))' };
        return (
          /* Mismo patrón que "Venta diaria" en Comercial: un solo Card
             contenedor con rejilla de tiles tintados, en vez de tarjetas
             sueltas. Replicado de components/comercial/VentasDiaPage.jsx
             para que las pantallas se lean como el mismo sistema. */
          <Card sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center',
                         justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                  Comisiones por pagar
                  {data?.total_agencias != null &&
                    ` · ${data.total_agencias} agencia${data.total_agencias === 1 ? '' : 's'}`}
                </Typography>
                {Number(resumen.total_pendiente_exigible) > 0 && (
                  <Chip size="small" label="Exigible hoy"
                    sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 800, fontSize: 12 }} />
                )}
              </Box>
              {/* gridAutoRows 1fr + stretch: todos los tiles al mismo alto
                  aunque la rejilla se envuelva. */}
              <Box sx={{ display: 'grid', gridTemplateColumns: cols,
                         gridAutoRows: '1fr', alignItems: 'stretch', gap: 1 }}>
                {kpis.map(k => <TileComision key={k.label} {...k} />)}
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* Dos tablas separadas — Opción A:
            (1) Agencias con comisión EXIGIBLE hoy (accionables, con antigüedad).
            (2) Agencias con SOLO comisiones futuras (bloqueadas hasta la salida). */}
      {(() => {
        const todas = data?.agencias || [];
        // Exigibles hoy: cualquier agencia con exigible > 0 (aunque también tenga futuras)
        const exigibles = todas.filter(a => (a.total_pendiente_exigible || 0) >= 1);
        // Solo futuras: sin exigible pero con futuro pendiente
        const soloFuturas = todas.filter(a =>
          (a.total_pendiente_exigible || 0) < 1 && (a.total_pendiente_futura || 0) >= 1
        );
        // Al día (opcional según toggle): sin exigible NI futura
        const alDia = todas.filter(a =>
          (a.total_pendiente_exigible || 0) < 1 && (a.total_pendiente_futura || 0) < 1
        );

        const renderRow = (a, opts = {}) => {
          const { bloqueada = false } = opts;
          const dias = a.antiguedad_max_dias || 0;
          const exigible = a.total_pendiente_exigible || 0;
          const futura = a.total_pendiente_futura || 0;
          const semaforo = bloqueada ? { bg: '#f1f5f9', color: '#64748b', label: '—' }
            : exigible < 1 ? { bg: '#dcfce7', color: '#15803d', label: 'Al día' }
            : dias <= 30 ? { bg: '#dcfce7', color: '#15803d', label: `${dias}d` }
            : dias <= 60 ? { bg: '#fef3c7', color: '#a16207', label: `${dias}d` }
            :               { bg: '#fee2e2', color: '#dc2626', label: `${dias}d` };
          return (
            <TableRow key={a.agencia_id} hover sx={bloqueada ? { opacity: 0.7, bgcolor: '#f8fafc' } : undefined}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: bloqueada ? '#64748b' : '#0f172a' }}>
                      {a.agencia_nombre}
                    </Typography>
                  </Box>
                  {bloqueada && (
                    <Chip icon={<LockIcon sx={{ fontSize: 12 }} />} label="Futura" size="small"
                      sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 10, height: 20, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell align="right">
                {bloqueada ? (
                  <Chip label={fmtMoney(futura)} size="small"
                    sx={{ bgcolor: '#e2e8f0', color: '#64748b', fontWeight: 700, fontSize: 12 }} />
                ) : exigible >= 1 ? (
                  <Chip label={fmtMoney(exigible)} size="small"
                    sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 700, fontSize: 12 }} />
                ) : (
                  <Chip label="—" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b' }} />
                )}
              </TableCell>
              <TableCell align="right">
                {futura >= 1 && !bloqueada ? (
                  <Chip icon={<LockIcon sx={{ fontSize: 12 }} />} label={fmtMoney(futura)} size="small"
                    sx={{ bgcolor: '#e2e8f0', color: '#64748b', fontWeight: 600, fontSize: 11 }} />
                ) : (
                  <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>
                )}
              </TableCell>
              <TableCell align="center">
                <Chip label={semaforo.label} size="small"
                  sx={{ bgcolor: semaforo.bg, color: semaforo.color, fontWeight: 700, fontSize: 11 }} />
              </TableCell>
              <TableCell align="center">
                {/* Dos chips: exigibles hoy (mostaza, accionable) + futuras (gris, en espera).
                    Antes se mostraba num_reservas (histórico completo, incluye pagadas) que era
                    engañoso en esta pantalla — aquí solo interesan las que aportan pendiente. */}
                <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                  {(a.num_pendientes_exigibles || 0) > 0 && (
                    <Chip label={a.num_pendientes_exigibles} size="small"
                      sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 700, fontSize: 12, height: 22 }} />
                  )}
                  {(a.num_pendientes_futuras || 0) > 0 && (
                    <Chip icon={<LockIcon sx={{ fontSize: 12 }} />} label={a.num_pendientes_futuras} size="small"
                      sx={{ bgcolor: '#e2e8f0', color: '#64748b', fontWeight: 700, fontSize: 12, height: 22 }} />
                  )}
                  {(a.num_pendientes_exigibles || 0) === 0 && (a.num_pendientes_futuras || 0) === 0 && (
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell align="center">
                <Button size="small" variant="outlined"
                  onClick={() => setDetalleAgencia({ ...a, _tipoPendiente: tipoPendiente })}
                  sx={{ textTransform: 'none' }}>
                  Ver detalle
                </Button>
              </TableCell>
            </TableRow>
          );
        };

        const tabla = (rows, bloqueadas = false) => (
          <TableContainer component={Paper} sx={{ ...cardStyle, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Agencia</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">
                    {bloqueadas ? 'Futura (bloqueada)' : 'Exigible'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">
                    {bloqueadas ? '—' : 'Futura'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Antigüedad</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Exigibles / Futuras</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{rows.map(a => renderRow(a, { bloqueada: bloqueadas }))}</TableBody>
            </Table>
          </TableContainer>
        );

        const hayNada = exigibles.length === 0 && soloFuturas.length === 0 && (!soloConPendiente ? alDia.length === 0 : true);

        return (
          <>
            {/* Sección 1: exigibles hoy */}
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#dc2626', mb: 1 }}>
              Exigibles hoy ({exigibles.length})
            </Typography>
            {exigibles.length === 0 ? (
              <Box sx={{ ...cardStyle, p: 3, textAlign: 'center', mb: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#10b981', mb: 1 }} />
                <Typography sx={{ color: '#134e4a', fontSize: 14, fontWeight: 600 }}>
                  No hay comisiones exigibles en este momento.
                </Typography>
              </Box>
            ) : (
              tabla(exigibles, false)
            )}

            {/* Sección 2: solo futuras (bloqueadas) */}
            {soloFuturas.length > 0 && (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#64748b', mb: 1, mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon sx={{ fontSize: 15 }} /> Solo futuras — bloqueadas hasta el día de la salida ({soloFuturas.length})
                </Typography>
                {tabla(soloFuturas, true)}
              </>
            )}

            {/* Sección 3: opcional, agencias al día */}
            {!soloConPendiente && alDia.length > 0 && (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#15803d', mb: 1, mt: 1 }}>
                  Al día ({alDia.length})
                </Typography>
                {tabla(alDia, false)}
              </>
            )}

            {hayNada && (
              <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
                <Typography sx={{ color: '#134e4a', fontSize: 15, fontWeight: 600 }}>
                  No hay agencias con comisiones para mostrar.
                </Typography>
              </Box>
            )}
          </>
        );
      })()}

      {detalleAgencia && (
        <DetalleAgenciaDialog
          agencia={detalleAgencia}
          onClose={() => { setDetalleAgencia(null); cargar(); }}
        />
      )}
    </Box>
  );
}


function KpiCard({ title, value, icon, color }) {
  return (
    <Card sx={{ ...cardStyle, p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: `${color}20`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#134e4a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </Typography>
      </Box>
    </Card>
  );
}


// ============================================================================
// DETALLE DE AGENCIA + LIQUIDAR
// ============================================================================
function DetalleAgenciaDialog({ agencia, onClose }) {
  const [reservasRaw, setReservasRaw] = useState([]);
  const [infoAgencia, setInfoAgencia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liquidarOpen, setLiquidarOpen] = useState(false);
  // Estado del diálogo "Pagar comisión" por reserva individual
  const [pagarReserva, setPagarReserva] = useState(null);

  // Modo heredado de la vista principal: 'exigibles' | 'futuras' | 'ambas'.
  // Determina qué reservas se muestran en el detalle para que el modal sea
  // consistente con lo que el operador filtró afuera.
  const modo = agencia._tipoPendiente || 'ambas';

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (agencia.fechaDesde) params.append('fecha_desde', agencia.fechaDesde);
      if (agencia.fechaHasta) params.append('fecha_hasta', agencia.fechaHasta);
      const res = await fetch(`${API_BASE}/turismo/comisiones/agencia/${agencia.agencia_id}/reservas?${params}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setReservasRaw(data.reservas || []);
      setInfoAgencia(data.agencia || null);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [agencia.agencia_id, agencia.fechaDesde, agencia.fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  // Filtro del detalle:
  //   1) Solo reservas con comisión pendiente > 0 (ya pagadas no aportan).
  //   2) Respetar el modo de la vista (exigibles / futuras / ambas).
  const reservas = reservasRaw.filter(r => {
    const pend = r.comision_pendiente !== undefined ? r.comision_pendiente : (r.pagada ? 0 : r.comision_valor);
    if ((pend || 0) < 1) return false;
    if (modo === 'exigibles' && r.es_futura) return false;
    if (modo === 'futuras' && !r.es_futura) return false;
    return true;
  });

  // Totales del header: solo cuentan las EXIGIBLES (servicio ya prestado).
  // Las futuras aparecen en la tabla pero bloqueadas — no acumulan en el CTA
  // "Liquidar período" porque no se pueden pagar aún.
  const reservasExigibles = reservas.filter(r => !r.es_futura);
  const totalPendiente = reservasExigibles.reduce((s, r) => s + (r.comision_pendiente !== undefined ? r.comision_pendiente : r.comision_valor), 0);
  const totalPendienteFuturo = reservas.filter(r => r.es_futura).reduce((s, r) => s + (r.comision_pendiente !== undefined ? r.comision_pendiente : r.comision_valor), 0);
  const totalAnticipado = reservas.reduce((s, r) => s + (r.comision_anticipada || 0), 0);
  const numPendientes = reservasExigibles.length;
  const numFuturas = reservas.filter(r => r.es_futura).length;
  const modoLabel = modo === 'exigibles' ? 'Solo exigibles hoy'
                  : modo === 'futuras' ? 'Solo futuras (bloqueadas)'
                  : 'Exigibles + Futuras';

  return (
    <>
      <StyledDialog open onClose={onClose}
        title={`${agencia.agencia_nombre}`}
        subtitle={`Reservas con comisión pendiente · ${modoLabel}`}
        icon={<BusinessIcon />} variant="compact" maxWidth="md">
        <StyledDialog.Body compact>
          {/* ¿Girar o cruzar? Es la decisión que se toma en esta pantalla, y
              hasta ahora obligaba a salir a la cartera para responderla. Ojo
              con el matiz: un saldo "a cargo de muelle" lo paga el turista, no
              la agencia — por eso el monto sale de deuda_agencia(), no del
              saldo pendiente de la reserva. */}
          {infoAgencia && (
            <Box sx={{
              mb: 1.5, px: 2, py: 1.25, borderRadius: 2,
              border: '1px solid', display: 'flex', gap: 3, flexWrap: 'wrap',
              alignItems: 'center',
              borderColor: infoAgencia.deuda_agencia > 0 ? '#7dd3fc' : '#e2e8f0',
              bgcolor: infoAgencia.deuda_agencia > 0 ? '#f0f9ff' : '#f8fafc',
            }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>
                  LE DEBES
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#d97706' }}>
                  {fmtMoney(totalPendiente + totalPendienteFuturo)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>
                  TE DEBE
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700,
                                  color: infoAgencia.deuda_agencia > 0 ? '#0369a1' : '#94a3b8' }}>
                  {infoAgencia.deuda_agencia > 0 ? fmtMoney(infoAgencia.deuda_agencia) : 'nada'}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 160 }}>
                {infoAgencia.deuda_agencia > 0 ? (
                  <>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0369a1' }}>
                      Se puede cruzar
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#475569' }}>
                      {infoAgencia.neto >= 0
                        ? `Cruzando quedarías debiéndole ${fmtMoney(infoAgencia.neto)}`
                        : `Su deuda supera la comisión: quedaría debiéndote ${fmtMoney(-infoAgencia.neto)}`}
                      {infoAgencia.deuda_detalle?.length > 0
                        && ` · ${infoAgencia.deuda_detalle.map(x => x.codigo_reserva).join(', ')}`}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    No tiene deuda contigo: no hay nada que cruzar, hay que girarle.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Resumen rápido */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Chip label={`${reservas.length} reserva${reservas.length === 1 ? '' : 's'} con pendiente`} size="small" color="primary" />
            {modo !== 'futuras' && (
              <Chip label={`${numPendientes} exigibles`} size="small" color="warning" />
            )}
            {modo !== 'futuras' && totalPendiente > 0 && (
              <Chip label={`Exigible: ${fmtMoney(totalPendiente)}`} size="small"
                sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 600 }} />
            )}
            {numFuturas > 0 && (
              <Chip icon={<LockIcon sx={{ fontSize: 12 }} />}
                label={`${numFuturas} futuras · ${fmtMoney(totalPendienteFuturo)}`} size="small"
                sx={{ bgcolor: '#e2e8f0', color: '#64748b', fontWeight: 600 }} />
            )}
            {totalAnticipado > 0 && (
              <Chip label={`Anticipado: ${fmtMoney(totalAnticipado)}`} size="small"
                sx={{ bgcolor: '#fefce8', color: '#a16207', fontWeight: 600 }} />
            )}
          </Box>

          {numPendientes > 0 && (
            <Button variant="contained" startIcon={<PaymentIcon />}
              onClick={() => setLiquidarOpen(true)}
              sx={{ background: gradientComision, boxShadow: `0 8px 20px ${shadowComision}`, alignSelf: 'flex-start' }}>
              Liquidar período ({fmtMoney(totalPendiente)})
            </Button>
          )}

          {loading ? <CircularProgress /> : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Total</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Comisión</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Anticipo</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Pagado</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Pendiente</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reservas.map(r => {
                    const _ant = r.comision_anticipada || 0;
                    const _abo = r.comision_abonada || 0;
                    // Lo que cubrió el corte o el cruce al que entró la reserva.
                    // Sin esto la fila no cerraba: comisión 280.000 − anticipo
                    // 40.000 daba 240.000, pero el pendiente decía 140.000 y los
                    // 100.000 del cruce no salían por ningún lado.
                    const _liq = r.comision_pagada_liquidacion || 0;
                    const _pag = _abo + _liq;
                    const _pend = r.comision_pendiente !== undefined ? r.comision_pendiente : (r.pagada ? 0 : r.comision_valor);
                    const _futura = !!r.es_futura;
                    return (
                    <TableRow key={r.id} hover
                      sx={_futura ? { opacity: 0.75, bgcolor: '#f8fafc' } : undefined}>
                      <TableCell sx={{ fontSize: 11 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{r.codigo_reserva}</span>
                          {_futura && (
                            <Tooltip title={`Se habilita el día de la salida (${r.fecha_salida || '—'})`}>
                              <Chip icon={<LockIcon sx={{ fontSize: 10 }} />} label="Futura" size="small"
                                sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 9, height: 18 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: _futura ? '#64748b' : undefined }}>{r.nombre_cliente}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11 }}>{fmtMoney(r.precio_total)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>
                        {fmtMoney(r.comision_valor)} <Typography component="span" sx={{ fontSize: 9, color: '#64748b' }}>({r.comision_porcentaje}%)</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, color: _ant > 0 ? '#a16207' : '#94a3b8' }}>
                        {_ant > 0 ? fmtMoney(_ant) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, color: _pag > 0 ? '#0d9488' : '#94a3b8' }}>
                        {_pag > 0 ? (
                          <Tooltip arrow title={
                            <Box sx={{ fontSize: 11, lineHeight: 1.7, maxWidth: 300 }}>
                              {_abo > 0 && <div>Abonos girados: <b>{fmtMoney(_abo)}</b></div>}
                              {_liq > 0 && (
                                <div>
                                  {r.liquidacion_metodo === 'cruce'
                                    ? 'Cruce contra deuda'
                                    : `Corte por ${r.liquidacion_metodo || 'liquidación'}`}
                                  : <b>{fmtMoney(_liq)}</b>
                                </div>
                              )}
                              {/* Un "#1033" suelto obliga a salir de la pantalla
                                  para entender de dónde salió la cifra. */}
                              {r.liquidacion && (
                                <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.25)', opacity: 0.9 }}>
                                  <div>Liquidación #{r.liquidacion.id}
                                    {r.liquidacion.fecha && ` · ${fmtFecha(r.liquidacion.fecha)}`}</div>
                                  {r.liquidacion.referencia && <div>Ref: {r.liquidacion.referencia}</div>}
                                  {r.liquidacion.cruce && (
                                    <>
                                      <div>Compensó {fmtMoney(r.liquidacion.cruce.deuda_cubierta)} de deuda
                                        {r.liquidacion.cruce.contra_reservas?.length > 0
                                          && ` (${r.liquidacion.cruce.contra_reservas.join(', ')})`}</div>
                                      {r.liquidacion.cruce.queda_a_favor > 0 && (
                                        <div>Quedó a favor de la agencia {fmtMoney(r.liquidacion.cruce.queda_a_favor)}</div>
                                      )}
                                      {r.liquidacion.cruce.aplicado_por && (
                                        <div>Aplicado por {r.liquidacion.cruce.aplicado_por}</div>
                                      )}
                                    </>
                                  )}
                                </Box>
                              )}
                            </Box>
                          }>
                            <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>
                              {fmtMoney(_pag)}
                            </span>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: _futura ? '#64748b' : (_pend > 0 ? '#d97706' : '#15803d') }}>
                        {fmtMoney(_pend)}
                      </TableCell>
                      <TableCell align="center">
                        {r.pagada ? (
                          <Chip label="Pagada" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
                        ) : _futura ? (
                          <Chip label="Bloqueada" size="small" sx={{ bgcolor: '#e2e8f0', color: '#64748b', fontSize: 10, height: 20 }} />
                        ) : (
                          <Chip label="Pendiente" size="small" color="warning" sx={{ fontSize: 10, height: 20 }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {!r.pagada && (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                            <Tooltip title={_futura
                              ? `Bloqueada — se habilita el día de la salida (${r.fecha_salida || '—'})`
                              : 'Pagar comisión con evidencia'}>
                              {/* Wrapper span porque Tooltip no funciona sobre botones disabled */}
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={_futura}
                                  startIcon={_futura ? <LockIcon sx={{ fontSize: 14 }} /> : <PaymentIcon sx={{ fontSize: 14 }} />}
                                  onClick={() => setPagarReserva(r)}
                                  sx={{
                                    textTransform: 'none', fontWeight: 700, fontSize: 11,
                                    px: 1.25, py: 0.4, minWidth: 0,
                                    background: _futura ? undefined : 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                                    boxShadow: _futura ? 'none' : '0 2px 6px rgba(20,184,166,0.35)',
                                    '&:hover': { background: _futura ? undefined : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' },
                                  }}
                                >
                                  {_futura ? 'Futura' : 'Pagar'}
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.PrimaryButton fullWidth onClick={onClose}>Cerrar</StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {liquidarOpen && (
        <LiquidarDialog
          agencia={agencia}
          totalPendiente={totalPendiente}
          numPendientes={numPendientes}
          onClose={(reloaded) => { setLiquidarOpen(false); if (reloaded) cargar(); }}
        />
      )}

      {pagarReserva && (
        <PagarComisionDialog
          reserva={pagarReserva}
          agenciaNombre={agencia.agencia_nombre}
          onClose={(reloaded) => { setPagarReserva(null); if (reloaded) cargar(); }}
        />
      )}
    </>
  );
}


// ============================================================================
// DIÁLOGO: LIQUIDAR
// ============================================================================
function LiquidarDialog({ agencia, totalPendiente, numPendientes, onClose }) {
  const [form, setForm] = useState({
    periodo_inicio: agencia.fechaDesde || firstDayOfMonth(),
    periodo_fin: agencia.fechaHasta || today(),
    metodo_pago: 'transferencia',
    referencia: '',
    notas: '',
  });
  const [saving, setSaving] = useState(false);
  const empresaId = getEmpresaId();
  const userId = parseInt(localStorage.getItem('user_id')) || null;

  // Comprobante con Vision — mismo componente que se usa en CxC
  const comprobante = useComprobanteUploader({
    apiBase: API_BASE,
    getAuthHeaders,
    empresaId,
    getMontoEsperado: () => totalPendiente,
  });

  // Al detectar la referencia en el voucher, pre-poblar el campo si está vacío
  const handleFileWrapped = async (file) => {
    const result = await comprobante.handleFile(file);
    const analJson = result?.analisis;
    if (analJson?.referencia && !form.referencia) {
      setForm(f => ({ ...f, referencia: String(analJson.referencia) }));
    }
  };

  // Alerta si el monto del voucher no cuadra con el total a pagar
  const discrepancia = React.useMemo(() => {
    const detectado = Number(comprobante.analisis?.monto || 0);
    if (!detectado || totalPendiente <= 0) return null;
    const diff = Math.abs(detectado - totalPendiente);
    const pct = (diff / detectado) * 100;
    if (pct <= 2) return null;
    return { detectado, distribuido: totalPendiente, diff, pct };
  }, [comprobante.analisis, totalPendiente]);

  const handleConfirmar = async () => {
    if (!window.confirm(`¿Confirmas el pago de ${fmtMoney(totalPendiente)} a ${agencia.agencia_nombre}?`)) return;
    setSaving(true);
    try {
      const body = {
        empresa_id: empresaId,
        agencia_id: agencia.agencia_id,
        periodo_inicio: form.periodo_inicio,
        periodo_fin: form.periodo_fin,
        metodo_pago: form.metodo_pago,
        referencia: form.referencia.trim() || null,
        notas: form.notas.trim() || null,
        comprobante_url: comprobante.url || null,
        registrado_por_user_id: userId,
      };
      const res = await fetch(`${API_BASE}/turismo/comisiones/liquidar`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Error: ${err.detail || 'No se pudo liquidar'}`);
        setSaving(false);
        return;
      }
      alert('Liquidación registrada correctamente');
      onClose(true);
    } catch (e) {
      console.error(e);
      alert('Error al liquidar');
    }
    setSaving(false);
  };

  return (
    <StyledDialog open onClose={() => !saving && onClose(false)}
      title="Registrar Liquidación" icon={<PaymentIcon />} variant="compact" maxWidth="md">
      <StyledDialog.Body compact>
        <Alert severity="info" sx={{ fontSize: 12 }}>
          Vas a marcar como pagadas <strong>{numPendientes}</strong> reservas pendientes
          de <strong>{agencia.agencia_nombre}</strong> por un total de <strong>{fmtMoney(totalPendiente)}</strong>.
        </Alert>

        <ComprobanteBlock
          url={comprobante.url}
          nombre={comprobante.nombre}
          subiendo={comprobante.subiendo}
          analizando={comprobante.analizando}
          analisis={comprobante.analisis}
          error={comprobante.error}
          onFile={handleFileWrapped}
          onQuitar={comprobante.quitar}
          discrepancia={discrepancia}
          fmtMoney={fmtMoney}
          titulo="Evidencia del pago (opcional)"
        />

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField sx={{ ...inputStyles, flex: 1, minWidth: 140 }} type="date" label="Período inicio"
            InputLabelProps={{ shrink: true }} value={form.periodo_inicio}
            onChange={e => setForm({ ...form, periodo_inicio: e.target.value })} />
          <TextField sx={{ ...inputStyles, flex: 1, minWidth: 140 }} type="date" label="Período fin"
            InputLabelProps={{ shrink: true }} value={form.periodo_fin}
            onChange={e => setForm({ ...form, periodo_fin: e.target.value })} />
        </Box>
        <FormControl sx={inputStyles}>
          <InputLabel>Método de pago</InputLabel>
          <Select value={form.metodo_pago} label="Método de pago"
            onChange={e => setForm({ ...form, metodo_pago: e.target.value })}>
            <MenuItem value="transferencia">Transferencia</MenuItem>
            <MenuItem value="efectivo">Efectivo</MenuItem>
            <MenuItem value="cheque">Cheque</MenuItem>
            <MenuItem value="nequi">Nequi</MenuItem>
            <MenuItem value="daviplata">Daviplata</MenuItem>
            <MenuItem value="otro">Otro</MenuItem>
          </Select>
        </FormControl>
        <TextField sx={inputStyles} label="Referencia / Número de transacción" fullWidth
          value={form.referencia} onChange={e => setForm({ ...form, referencia: e.target.value })} />
        <TextField sx={inputStyles} label="Notas" fullWidth multiline rows={2}
          value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} />
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={() => onClose(false)} disabled={saving} />
        <StyledDialog.PrimaryButton fullWidth onClick={handleConfirmar} loading={saving}>
          Confirmar pago
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


// ============================================================================
// DIÁLOGO: PAGAR COMISIÓN DE UNA RESERVA ESPECÍFICA
// ============================================================================
function PagarComisionDialog({ reserva, agenciaNombre, onClose }) {
  const pendiente = Number(reserva.comision_pendiente || 0);
  const [form, setForm] = useState({
    fecha_pago: today(),
    metodo_pago: 'transferencia',
    referencia: '',
    notas: '',
    // Monto del abono — por defecto, el pendiente completo. El operador
    // puede bajarlo para hacer un abono parcial (ej: $440k de $880k).
    monto: String(pendiente),
  });
  const [saving, setSaving] = useState(false);
  const empresaId = getEmpresaId();
  const userId = parseInt(localStorage.getItem('user_id')) || null;

  const montoAbono = Number(form.monto) || 0;
  const restanteDespues = Math.max(pendiente - montoAbono, 0);
  const esPagoTotal = restanteDespues < 0.01 && montoAbono > 0;
  const excedePendiente = montoAbono > pendiente + 0.01;
  const montoInvalido = montoAbono <= 0 || excedePendiente;

  const comprobante = useComprobanteUploader({
    apiBase: API_BASE,
    getAuthHeaders,
    empresaId,
    // El monto esperado para el análisis Vision es el del abono, no el
    // pendiente total (así la alerta de discrepancia es correcta cuando
    // subís el voucher de un abono parcial).
    getMontoEsperado: () => montoAbono || pendiente,
  });

  const handleFileWrapped = async (file) => {
    const result = await comprobante.handleFile(file);
    const analJson = result?.analisis;
    if (analJson?.referencia && !form.referencia) {
      setForm(f => ({ ...f, referencia: String(analJson.referencia) }));
    }
  };

  const discrepancia = React.useMemo(() => {
    const detectado = Number(comprobante.analisis?.monto || 0);
    if (!detectado || montoAbono <= 0) return null;
    const diff = Math.abs(detectado - montoAbono);
    const pct = (diff / detectado) * 100;
    if (pct <= 2) return null;
    return { detectado, distribuido: montoAbono, diff, pct };
  }, [comprobante.analisis, montoAbono]);

  const handleConfirmar = async () => {
    if (montoInvalido) return;
    const msg = esPagoTotal
      ? `¿Confirmas el pago total de ${fmtMoney(montoAbono)} por la comisión de ${reserva.codigo_reserva}?`
      : `¿Confirmas el abono parcial de ${fmtMoney(montoAbono)}? Quedarán ${fmtMoney(restanteDespues)} pendientes.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const body = {
        empresa_id: empresaId,
        metodo_pago: form.metodo_pago,
        fecha_pago: form.fecha_pago,
        referencia: form.referencia.trim() || null,
        notas: form.notas.trim() || null,
        comprobante_url: comprobante.url || null,
        monto: montoAbono,
        registrado_por_user_id: userId,
      };
      const res = await fetch(
        `${API_BASE}/turismo/comisiones/reserva/${reserva.id}/liquidar`,
        { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(`Error: ${json.detail || 'No se pudo registrar el pago'}`);
        setSaving(false);
        return;
      }
      alert(json.detail || (esPagoTotal ? 'Comisión pagada correctamente' : 'Abono registrado'));
      onClose(true);
    } catch (e) {
      console.error(e);
      alert('Error al registrar el pago');
    }
    setSaving(false);
  };

  return (
    <StyledDialog open onClose={() => !saving && onClose(false)}
      title="Pagar comisión de reserva"
      icon={<PaymentIcon />} variant="compact" maxWidth="sm">
      <StyledDialog.Body compact>
        <Alert severity="info" sx={{ fontSize: 12 }}>
          <b>{reserva.codigo_reserva}</b> · {reserva.nombre_cliente || 'Sin titular'}
          <br />
          Agencia: <b>{agenciaNombre}</b>
          <br />
          Comisión pendiente: <b>{fmtMoney(pendiente)}</b>
          {reserva.comision_anticipada > 0 && (
            <>
              {' '}(comisión total {fmtMoney(reserva.comision_efectiva || reserva.comision_valor)}
              {' '}− anticipada {fmtMoney(reserva.comision_anticipada)}
              {reserva.comision_abonada > 0
                && ` − abonos previos ${fmtMoney(reserva.comision_abonada)}`}
              )
            </>
          )}
        </Alert>

        <ComprobanteBlock
          url={comprobante.url}
          nombre={comprobante.nombre}
          subiendo={comprobante.subiendo}
          analizando={comprobante.analizando}
          analisis={comprobante.analisis}
          error={comprobante.error}
          onFile={handleFileWrapped}
          onQuitar={comprobante.quitar}
          discrepancia={discrepancia}
          fmtMoney={fmtMoney}
          titulo="Evidencia del pago (opcional)"
        />

        {/* Monto del abono con toggle rápido total/mitad */}
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              sx={{ ...inputStyles, flex: 1, minWidth: 180 }}
              label="Monto del abono"
              type="number"
              inputProps={{ min: 0, max: pendiente, step: 1000 }}
              value={form.monto}
              onChange={e => setForm({ ...form, monto: e.target.value.replace(/[^\d.]/g, '') })}
              error={excedePendiente}
              helperText={
                excedePendiente
                  ? `Excede el pendiente (${fmtMoney(pendiente)})`
                  : (esPagoTotal
                      ? `Pago total — la comisión quedará saldada`
                      : `Abono parcial — quedan ${fmtMoney(restanteDespues)} pendientes`)
              }
            />
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.75 }}>
              <Button size="small" variant="outlined"
                onClick={() => setForm({ ...form, monto: String(pendiente) })}
                sx={{ textTransform: 'none', fontSize: 11, py: 0.5, px: 1 }}>
                Total ({fmtMoney(pendiente)})
              </Button>
              <Button size="small" variant="outlined"
                onClick={() => setForm({ ...form, monto: String(Math.round(pendiente / 2)) })}
                sx={{ textTransform: 'none', fontSize: 11, py: 0.5, px: 1 }}>
                50%
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField sx={{ ...inputStyles, flex: 1, minWidth: 140 }} type="date" label="Fecha del pago"
            InputLabelProps={{ shrink: true }} value={form.fecha_pago}
            onChange={e => setForm({ ...form, fecha_pago: e.target.value })} />
          <FormControl sx={{ ...inputStyles, flex: 1, minWidth: 160 }}>
            <InputLabel>Método de pago</InputLabel>
            <Select value={form.metodo_pago} label="Método de pago"
              onChange={e => setForm({ ...form, metodo_pago: e.target.value })}>
              <MenuItem value="transferencia">Transferencia</MenuItem>
              <MenuItem value="efectivo">Efectivo</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="nequi">Nequi</MenuItem>
              <MenuItem value="daviplata">Daviplata</MenuItem>
              <MenuItem value="cruce">Cruce</MenuItem>
              <MenuItem value="otro">Otro</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TextField sx={inputStyles} label="Referencia / Número de transacción" fullWidth
          value={form.referencia} onChange={e => setForm({ ...form, referencia: e.target.value })} />
        <TextField sx={inputStyles} label="Notas" fullWidth multiline rows={2}
          value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} />
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={() => onClose(false)} disabled={saving} />
        <StyledDialog.PrimaryButton fullWidth onClick={handleConfirmar}
          loading={saving} disabled={montoInvalido}>
          {esPagoTotal ? `Pagar ${fmtMoney(montoAbono)}` : `Registrar abono ${fmtMoney(montoAbono)}`}
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


// ============================================================================
// DIÁLOGO: OVERRIDE % COMISIÓN POR RESERVA
// ============================================================================
/**
 * Cambia el % de comisión de una reserva.
 *
 * Ya NO se usa desde "Comisiones por pagar": ahí no tiene sentido —es un
 * módulo de cuentas por pagar, no de edición de reservas— y encima el backend
 * rechaza toda reserva que ya tenga liquidación, que es justo donde aparecía
 * el botón desde que las de corte parcial siguen contando como pendientes.
 * Si hace falta, va en la pantalla de la reserva, antes de que se liquide.
 */
// eslint-disable-next-line no-unused-vars
function OverrideDialog({ reserva, onClose }) {
  const [porcentaje, setPorcentaje] = useState(String(reserva.comision_porcentaje || 0));
  const [saving, setSaving] = useState(false);

  const nuevoValor = (parseFloat(porcentaje) || 0) * reserva.precio_total / 100;

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/comisiones/reservas/${reserva.id}/override`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({ porcentaje_nuevo: parseFloat(porcentaje), recalcular: true }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Error: ${err.detail || 'No se pudo actualizar'}`);
        setSaving(false);
        return;
      }
      onClose(true);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <StyledDialog open onClose={() => !saving && onClose(false)}
      title="Editar comisión de reserva" icon={<EditIcon />} variant="compact" maxWidth="xs">
      <StyledDialog.Body compact>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>Reserva</Typography>
          <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{reserva.codigo_reserva}</Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            Cliente: {reserva.nombre_cliente} · Total: {fmtMoney(reserva.precio_total)}
          </Typography>
        </Box>
        <TextField sx={inputStyles} label="% de comisión" type="number" fullWidth
          value={porcentaje} onChange={e => setPorcentaje(e.target.value)}
          inputProps={{ min: 0, max: 100, step: 0.1 }} />
        <Alert severity="info" sx={{ fontSize: 12 }}>
          Nueva comisión calculada: <strong>{fmtMoney(nuevoValor)}</strong>
        </Alert>
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={() => onClose(false)} disabled={saving} />
        <StyledDialog.PrimaryButton fullWidth onClick={handleGuardar} loading={saving}>
          Guardar
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


// ============================================================================
// TAB: HISTORIAL DE LIQUIDACIONES
// ============================================================================
/**
 * EvidenciaPago — celda visual de la columna "Evidencia" en Comisiones pagadas.
 *
 * Comportamiento por método:
 *   · anticipo → chip "Retención agencia" (no requiere evidencia; la agencia
 *     se descontó del cobro al cliente, no hay pago que documentar).
 *   · cruce → chip "Cruce contable" (se compensa con CxC del cliente; el
 *     comprobante formal es el registro del cruce, no un archivo).
 *   · con comprobante_url → thumbnail si es imagen, badge PDF si es PDF,
 *     o link genérico. Etiqueta según el método (Voucher/Recibo/Evidencia).
 *   · sin comprobante_url ni método especial → "—" con tooltip.
 */
function EvidenciaPago({ liq }) {
  const metodo = (liq.metodo_pago || '').toLowerCase();
  const url = liq.comprobante_url;

  // Métodos que por diseño NO tienen archivo adjunto
  if (metodo === 'anticipo') {
    return (
      <Tooltip title="Anticipo: la agencia retuvo su comisión del cobro al cliente. No hay pago de Entretur que documentar.">
        <Chip label="Retención" size="small"
          sx={{ bgcolor: '#fefce8', color: '#a16207', fontWeight: 600, fontSize: 10 }} />
      </Tooltip>
    );
  }
  if (metodo === 'cruce') {
    return (
      <Tooltip title="Cruce contable: la comisión se compensó con CxC del cliente.">
        <Chip icon={<CompareArrowsIcon sx={{ fontSize: 12 }} />} label="Cruce" size="small"
          sx={{ bgcolor: '#ecfdf5', color: '#0d9488', fontWeight: 600, fontSize: 10 }} />
      </Tooltip>
    );
  }

  if (!url) {
    return (
      <Tooltip title="Sin evidencia adjunta">
        <span style={{ color: '#cbd5e1', fontSize: 14 }}>—</span>
      </Tooltip>
    );
  }

  const href = url.startsWith('http')
    ? url
    : `${API_BASE.replace(/\/api$/, '')}${url}`;
  const esImagen = /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url);
  const esPdf = /\.pdf(\?|$)/i.test(url);

  // Etiqueta según método (para que el operador sepa qué está viendo)
  const label = metodo === 'transferencia' ? 'Voucher'
              : metodo === 'efectivo'      ? 'Recibo'
              : metodo === 'cheque'        ? 'Cheque'
              : 'Evidencia';

  if (esImagen) {
    return (
      <Tooltip title={`Ver ${label.toLowerCase()} (imagen)`}>
        <Box component="a" href={href} target="_blank" rel="noopener"
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            textDecoration: 'none', color: '#0891b2',
          }}>
          <Box component="img" src={href} alt={label}
            sx={{
              width: 32, height: 32, borderRadius: 1, objectFit: 'cover',
              border: '1px solid #e2e8f0',
            }} />
          <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{label}</Typography>
        </Box>
      </Tooltip>
    );
  }
  if (esPdf) {
    return (
      <Tooltip title={`Ver ${label.toLowerCase()} (PDF)`}>
        <Chip clickable component="a" href={href} target="_blank" rel="noopener"
          icon={<ReceiptLongIcon sx={{ fontSize: 14 }} />}
          label={`${label} PDF`} size="small"
          sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600, fontSize: 10 }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Ver ${label.toLowerCase()}`}>
      <Chip clickable component="a" href={href} target="_blank" rel="noopener"
        icon={<ReceiptLongIcon sx={{ fontSize: 14 }} />}
        label={label} size="small"
        sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: 10 }} />
    </Tooltip>
  );
}


/**
 * DetalleReservasLiquidacion — Contenido del expandible en cada fila de "Comisiones pagadas".
 * Muestra las reservas cubiertas por la liquidación con el desglose de comisión.
 */
function DetalleReservasLiquidacion({ det, liq }) {
  if (!det) {
    return <Box sx={{ p: 2, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Sin datos.</Box>;
  }
  if (det.loading) {
    return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  }
  const rows = det.data?.reservas || [];
  if (rows.length === 0) {
    return <Box sx={{ p: 2, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Esta liquidación no tiene reservas asociadas.</Box>;
  }
  const totalPagado = rows.reduce((s, r) => s + (r.pagada_en_esta_liquidacion || 0), 0);
  const isAnticipo = (det.data?.metodo_pago || '').toLowerCase() === 'anticipo';

  // Preview de la evidencia del pago (voucher/transferencia/recibo) — el mismo
  // para toda la liquidación, se muestra una vez arriba del expandible.
  const url = liq?.comprobante_url;
  const href = url ? (url.startsWith('http') ? url : `${API_BASE.replace(/\/api$/, '')}${url}`) : null;
  const esImagen = url && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url);

  return (
    <Box sx={{
      p: 2,
      bgcolor: '#f0fdfa',                    // teal-50, tono suave que marca "expandido"
      borderLeft: '4px solid #0d9488',       // barra teal a la izquierda que ancla el detalle a la fila padre
    }}>
      {/* Encabezado con evidencia del pago — en xs va apilado, desde sm en fila */}
      <Box sx={{
        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1, sm: 2 }, mb: 2, p: 1.5,
        bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0',
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
            Evidencia del pago
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a', mt: 0.25 }}>
            {liq?.metodo_pago === 'anticipo' ? 'Retención de la agencia · sin evidencia externa'
              : liq?.metodo_pago === 'cruce' ? 'Cruce contable con CxC del cliente'
              : `${liq?.metodo_pago || 'Pago'} ${liq?.referencia ? `· ref. ${liq.referencia}` : ''}`}
          </Typography>
        </Box>
        {href && esImagen && (
          <Tooltip title="Abrir evidencia en pestaña nueva">
            <Box component="a" href={href} target="_blank" rel="noopener"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: '#0891b2' }}>
              <Box component="img" src={href} alt="evidencia"
                sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', border: '1px solid #e2e8f0' }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Ver imagen</Typography>
            </Box>
          </Tooltip>
        )}
        {href && !esImagen && (
          <Chip clickable component="a" href={href} target="_blank" rel="noopener"
            icon={<ReceiptLongIcon sx={{ fontSize: 14 }} />} label="Ver comprobante" size="small"
            sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }} />
        )}
        {!href && !isAnticipo && liq?.metodo_pago !== 'cruce' && (
          <Chip label="Sin evidencia adjunta" size="small"
            sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600, fontSize: 10 }} />
        )}
      </Box>

      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Reservas incluidas en este pago ({rows.length})
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: { xs: 480, sm: 'auto' } }}>
          <TableHead>
            {/* Anticipada y Aún pendiente se ocultan en xs/sm para simplificar en mobile. */}
            <TableRow sx={{ bgcolor: '#fff' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cliente</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Comisión total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, display: { xs: 'none', md: 'table-cell' } }}>Anticipada</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Pagada aquí</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, display: { xs: 'none', sm: 'table-cell' } }}>Aún pendiente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 600 }}>{r.codigo_reserva}</TableCell>
                <TableCell sx={{ fontSize: 11.5 }}>{r.nombre_cliente}</TableCell>
                <TableCell align="right" sx={{ fontSize: 11.5 }}>{fmtMoney(r.comision_valor)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 11.5, color: r.comision_anticipada > 0 ? '#a16207' : '#94a3b8', display: { xs: 'none', md: 'table-cell' } }}>
                  {r.comision_anticipada > 0 ? fmtMoney(r.comision_anticipada) : '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>
                  {fmtMoney(r.pagada_en_esta_liquidacion)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 11.5, color: r.comision_aun_pendiente > 0 ? '#d97706' : '#15803d', fontWeight: r.comision_aun_pendiente > 0 ? 700 : 400, display: { xs: 'none', sm: 'table-cell' } }}>
                  {r.comision_aun_pendiente > 0 ? fmtMoney(r.comision_aun_pendiente) : '$0'}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
              <TableCell colSpan={3} sx={{ fontWeight: 700, fontSize: 11.5, display: { xs: 'table-cell', md: 'none' } }}>
                Total {isAnticipo ? 'retenido' : 'pagado'}
              </TableCell>
              <TableCell colSpan={4} sx={{ fontWeight: 700, fontSize: 11.5, display: { xs: 'none', md: 'table-cell' } }}>
                Total {isAnticipo ? 'retenido (anticipo)' : 'pagado en esta liquidación'}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 800, color: '#0d9488' }}>
                {fmtMoney(totalPagado)}
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {isAnticipo && (
        <Typography sx={{ fontSize: 11, color: '#a16207', mt: 1, fontStyle: 'italic' }}>
          ⓘ Liquidación tipo anticipo: la agencia se retuvo la comisión al cobrar al cliente — Entretur no desembolsó.
        </Typography>
      )}
    </Box>
  );
}


export function HistorialTab() {
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revertirCruce, setRevertirCruce] = useState(null);
  // Set de ids de liquidaciones expandidas + caché de reservas por liquidación.
  // Lazy: solo se carga el detalle cuando el usuario expande la fila.
  const [expandidos, setExpandidos] = useState(new Set());
  const [detalleReservas, setDetalleReservas] = useState({});   // { [liqId]: {loading, data} }
  const empresaId = getEmpresaId();
  // Filtros: por defecto última semana (hoy - 7 días → hoy).
  const _hoy = new Date();
  const _hace7 = new Date(_hoy.getTime() - 7 * 24 * 60 * 60 * 1000);
  const _toISO = (d) => d.toISOString().slice(0, 10);
  const [filtros, setFiltros] = useState({
    fecha_desde: _toISO(_hace7),
    fecha_hasta: _toISO(_hoy),
    agencia_id: '',
    metodos: [],   // multi-select: [] = todos
  });
  const [agenciasDisponibles, setAgenciasDisponibles] = useState([]);
  // Métodos posibles (fijos según el modelo — coincide con lo que el backend acepta)
  const METODOS_OPCIONES = [
    { key: 'transferencia', label: 'Transferencia' },
    { key: 'anticipo',      label: 'Anticipo (retención agencia)' },
    { key: 'cruce',         label: 'Cruce' },
    { key: 'efectivo',      label: 'Efectivo' },
    { key: 'otro',          label: 'Otro' },
  ];

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, limit: '500' });
      if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
      if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
      if (filtros.agencia_id) params.append('agencia_id', filtros.agencia_id);
      if (filtros.metodos && filtros.metodos.length > 0) {
        params.append('metodo_pago', filtros.metodos.join(','));
      }
      const res = await fetch(`${API_BASE}/turismo/comisiones/liquidaciones?${params}`,
        { headers: getAuthHeaders() });
      setLiquidaciones(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  // Cargar catálogo de agencias que han tenido al menos 1 liquidación (para el dropdown).
  // Se hace UNA sola vez sin filtro de fecha para tener el listado completo.
  useEffect(() => {
    fetch(`${API_BASE}/turismo/comisiones/liquidaciones?empresa_id=${empresaId}&limit=500`,
      { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(rows => {
        const uniq = new Map();
        (rows || []).forEach(l => {
          if (l.agencia_id && !uniq.has(l.agencia_id)) {
            uniq.set(l.agencia_id, l.agencia_nombre || `Agencia #${l.agencia_id}`);
          }
        });
        setAgenciasDisponibles(
          Array.from(uniq.entries())
            .map(([id, nombre]) => ({ id, nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
        );
      })
      .catch(() => {});
  }, [empresaId]);

  // KPIs derivados del resultado filtrado
  const totalPagado = liquidaciones.reduce((s, l) => s + Number(l.total_comisiones || 0), 0);
  const totalLiquidaciones = liquidaciones.length;

  // Toggle expand/collapse de una liquidación. Al expandir, si no está en caché,
  // se pide el detalle de reservas asociadas al backend.
  const toggleExpand = async (liq) => {
    const s = new Set(expandidos);
    if (s.has(liq.id)) {
      s.delete(liq.id);
      setExpandidos(s);
      return;
    }
    s.add(liq.id);
    setExpandidos(s);
    if (!detalleReservas[liq.id]) {
      setDetalleReservas(prev => ({ ...prev, [liq.id]: { loading: true, data: null } }));
      try {
        const res = await fetch(`${API_BASE}/turismo/comisiones/liquidaciones/${liq.id}/reservas`,
          { headers: getAuthHeaders() });
        const data = res.ok ? await res.json() : null;
        setDetalleReservas(prev => ({ ...prev, [liq.id]: { loading: false, data } }));
      } catch (e) {
        setDetalleReservas(prev => ({ ...prev, [liq.id]: { loading: false, data: null } }));
      }
    }
  };

  const handleRevertir = async (liq) => {
    if (liq.cruce_id) {
      setRevertirCruce(liq);
      return;
    }
    if (!window.confirm(`¿Revertir la liquidación de ${liq.agencia_nombre} por ${fmtMoney(liq.total_comisiones)}? Las reservas volverán a estado pendiente.`)) return;
    const res = await fetch(`${API_BASE}/turismo/comisiones/liquidaciones/${liq.id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (res.ok) cargar();
  };

  const filtrosPanel = (
    <Paper sx={{ ...cardStyle, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
          value={filtros.fecha_desde}
          onChange={e => setFiltros({ ...filtros, fecha_desde: e.target.value })}
          sx={{ minWidth: 150 }} />
        <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
          value={filtros.fecha_hasta}
          onChange={e => setFiltros({ ...filtros, fecha_hasta: e.target.value })}
          sx={{ minWidth: 150 }} />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Agencia</InputLabel>
          <Select label="Agencia" value={filtros.agencia_id}
            onChange={e => setFiltros({ ...filtros, agencia_id: e.target.value })}>
            <MenuItem value="">Todas</MenuItem>
            {agenciasDisponibles.map(a => (
              <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Método</InputLabel>
          <Select label="Método" multiple
            value={filtros.metodos}
            onChange={e => setFiltros({ ...filtros, metodos: e.target.value })}
            renderValue={(selected) => selected.length === 0
              ? 'Todos'
              : selected.map(v => METODOS_OPCIONES.find(o => o.key === v)?.label || v).join(', ')
            }>
            {METODOS_OPCIONES.map(o => (
              <MenuItem key={o.key} value={o.key}>
                <Checkbox checked={filtros.metodos.indexOf(o.key) > -1} size="small" />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" variant="outlined"
          onClick={() => setFiltros({
            fecha_desde: _toISO(_hace7),
            fecha_hasta: _toISO(_hoy),
            agencia_id: '',
            metodos: [],
          })}
          sx={{ textTransform: 'none' }}>
          Limpiar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              Liquidaciones
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
              {totalLiquidaciones}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              Total pagado
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#059669' }}>
              {fmtMoney(totalPagado)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  if (loading) return (<>{filtrosPanel}<Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box></>);

  if (liquidaciones.length === 0) {
    return (
      <>
        {filtrosPanel}
        <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
          <ReceiptLongIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 1.5 }} />
          <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>Sin liquidaciones en el rango</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            Ajusta las fechas o quita el filtro de agencia para ver más.
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      {filtrosPanel}
      <TableContainer component={Paper} sx={{ ...cardStyle, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            {/* Responsivo: ocultamos Período y Referencia en < md, y Evidencia
                en < sm (siguen accesibles al expandir la fila). */}
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Agencia</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, display: { xs: 'none', md: 'table-cell' } }}>Período</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Reservas</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Total</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, display: { xs: 'none', sm: 'table-cell' } }}>Método</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, display: { xs: 'none', md: 'table-cell' } }}>Referencia</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, display: { xs: 'none', sm: 'table-cell' } }} align="center">Evidencia</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liquidaciones.map(l => {
              const open = expandidos.has(l.id);
              const det = detalleReservas[l.id];
              return (
              <React.Fragment key={l.id}>
              <TableRow hover sx={{
                '& > *': { borderBottom: open ? 'unset' : undefined },
                cursor: 'pointer',
                // Fila padre resaltada cuando el expandible está abierto (paleta Turismo).
                bgcolor: open ? '#f0fdfa' : undefined,
              }}
                onClick={() => toggleExpand(l)}>
                <TableCell>
                  <IconButton size="small" sx={{ color: open ? '#0d9488' : undefined }}>
                    {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{l.fecha_pago?.slice(0, 10)}</TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{l.agencia_nombre}</TableCell>
                <TableCell sx={{ fontSize: 11, color: '#64748b', display: { xs: 'none', md: 'table-cell' } }}>
                  {l.periodo_inicio} → {l.periodo_fin}
                </TableCell>
                <TableCell align="center">
                  <Chip label={l.total_reservas} size="small" color="primary" />
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>
                  {fmtMoney(l.total_comisiones)}
                </TableCell>
                <TableCell sx={{ fontSize: 12, display: { xs: 'none', sm: 'table-cell' } }}>
                  {l.metodo_pago === 'cruce' ? (
                    <Chip label="CRUCE" size="small" icon={<CompareArrowsIcon sx={{ fontSize: 14 }} />}
                      sx={{ bgcolor: '#ecfdf5', color: '#0d9488', fontWeight: 700, fontSize: 10 }} />
                  ) : l.metodo_pago === 'anticipo' ? (
                    <Chip label="ANTICIPO" size="small"
                      sx={{ bgcolor: '#fefce8', color: '#a16207', fontWeight: 700, fontSize: 10 }} />
                  ) : (
                    <Chip label={l.metodo_pago || '—'} size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 11, color: '#64748b', display: { xs: 'none', md: 'table-cell' } }}>{l.referencia || '—'}</TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <EvidenciaPago liq={l} />
                </TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={l.cruce_id ? 'Revertir cruce' : 'Revertir esta liquidación'}>
                    <IconButton size="small" color="warning" onClick={() => handleRevertir(l)}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={10} sx={{ py: 0, px: { xs: 0.5, sm: 2 }, borderBottom: open ? '1px solid #e2e8f0' : 'none' }}>
                  <Collapse in={open} unmountOnExit>
                    <DetalleReservasLiquidacion det={det} liq={l} />
                  </Collapse>
                </TableCell>
              </TableRow>
              </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {revertirCruce && (
          <RevertirCruceDialog liq={revertirCruce} onClose={() => setRevertirCruce(null)} onReverted={() => { setRevertirCruce(null); cargar(); }} />
        )}
      </TableContainer>
    </>
  );
}


function RevertirCruceDialog({ liq, onClose, onReverted }) {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!motivo || motivo.trim().length < 3) {
      setError('El motivo es obligatorio (mín. 3 caracteres).');
      return;
    }
    setLoading(true); setError(null);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/turismo/cruce/${liq.cruce_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          motivo,
          revertido_por_user_id: user.id || null,
          revertido_por_nombre: user.nombre || user.name || user.email || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Error');
      onReverted();
    } catch (e) {
      setError(e.message || 'Error revirtiendo cruce');
    }
    setLoading(false);
  };

  return (
    <StyledDialog open onClose={onClose}
      title="Revertir cruce"
      subtitle={`${liq.agencia_nombre} — ${fmtMoney(liq.total_comisiones)}`}
      icon={<UndoIcon />} variant="compact" maxWidth="xs">
      <StyledDialog.Body compact>
        <Alert severity="warning" sx={{ fontSize: 12, mb: 2 }}>
          Esta acción revertirá los pagos a reservas y devolverá las comisiones a estado pendiente.
        </Alert>
        <TextField sx={inputStyles} fullWidth multiline rows={2} required label="Motivo de la reversión"
          placeholder="Ej. Error en el cálculo, doble cruce, etc."
          value={motivo} onChange={e => setMotivo(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: 12 }}>{error}</Alert>}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={onClose}>Cancelar</StyledDialog.CancelButton>
        <StyledDialog.PrimaryButton fullWidth loading={loading} onClick={submit}>
          Revertir
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


// ============================================================================
// TAB: CRUCE COMISIONES vs DEUDA
// ============================================================================
export function CruceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soloCompensables, setSoloCompensables] = useState(true);
  const [detalleAgId, setDetalleAgId] = useState(null);
  const [confirmarAg, setConfirmarAg] = useState(null);
  const empresaId = getEmpresaId();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        empresa_id: empresaId,
        solo_compensables: String(soloCompensables),
      });
      const res = await fetch(`${API_BASE}/turismo/cruce/resumen?${params}`, { headers: getAuthHeaders() });
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, soloCompensables]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

  const kpis = data?.kpis || {};
  const filas = data?.agencias || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Filtro</InputLabel>
          <Select label="Filtro" value={soloCompensables ? '1' : '0'} onChange={e => setSoloCompensables(e.target.value === '1')}>
            <MenuItem value="1">Solo agencias con cruce posible</MenuItem>
            <MenuItem value="0">Todas las agencias</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
          El cruce se calcula sobre TODO el acumulado (sin filtro de fecha) — FIFO sobre reservas más antiguas.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard title="Agencias compensables" value={String(kpis.num_agencias_compensables || 0)} icon={<CompareArrowsIcon />} color="#0d9488" />
        <KpiCard title="Total deuda" value={fmtMoney(kpis.total_deuda)} icon={<AccountBalanceIcon />} color="#dc2626" />
        <KpiCard title="Total comisión pendiente" value={fmtMoney(kpis.total_comision_pendiente)} icon={<TrendingUpIcon />} color="#a16207" />
        <KpiCard title="Total compensable" value={fmtMoney(kpis.total_compensable)} icon={<CheckCircleIcon />} color="#0d9488" />
      </Box>

      {(!filas || filas.length === 0) ? (
        <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: '#10b981', mb: 1.5 }} />
          <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>
            {soloCompensables ? 'No hay agencias con cruce posible' : 'No hay agencias registradas'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={cardStyle}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Agencia</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Deuda agencia</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Comisión agencia</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Cruce neto</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">A compensar</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filas.map(f => {
                const netoColor = f.neto > 0 ? '#dc2626' : (f.neto < 0 ? '#0d9488' : '#64748b');
                const netoLabel = f.neto > 0
                  ? `Agencia debe ${fmtMoney(f.neto)}`
                  : f.neto < 0
                    ? `Empresa debe ${fmtMoney(Math.abs(f.neto))}`
                    : 'Compensa exacto';
                return (
                  <TableRow key={f.agencia_id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{f.agencia_nombre}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                        {f.num_reservas_con_deuda} reservas con deuda · {f.num_comisiones_pendientes} comisiones pendientes
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, color: f.total_deuda > 0 ? '#dc2626' : '#64748b' }}>
                      {fmtMoney(f.total_deuda)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, color: f.total_comision_pendiente > 0 ? '#a16207' : '#64748b' }}>
                      {fmtMoney(f.total_comision_pendiente)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip size="small" label={netoLabel} sx={{
                        bgcolor: f.neto > 0 ? '#fef2f2' : (f.neto < 0 ? '#f0fdfa' : '#f8fafc'),
                        color: netoColor, fontWeight: 600, fontSize: 11,
                      }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 14, fontWeight: 700, color: f.monto_compensable > 0 ? '#0d9488' : '#94a3b8' }}>
                      {fmtMoney(f.monto_compensable)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button size="small" variant="outlined" onClick={() => setDetalleAgId(f.agencia_id)} sx={{ textTransform: 'none' }}>
                          Ver detalle
                        </Button>
                        <Button size="small" variant="contained" disabled={f.monto_compensable <= 0}
                          onClick={() => setConfirmarAg(f)}
                          sx={{ textTransform: 'none', background: gradientComision, '&:hover': { background: gradientComision } }}>
                          Aplicar cruce
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {detalleAgId && (
        <DetalleCruceDialog agenciaId={detalleAgId} onClose={() => setDetalleAgId(null)} />
      )}
      {confirmarAg && (
        <ConfirmarCruceDialog fila={confirmarAg} onClose={() => setConfirmarAg(null)} onApplied={() => { setConfirmarAg(null); cargar(); }} />
      )}
    </Box>
  );
}


function DetalleCruceDialog({ agenciaId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const res = await fetch(`${API_BASE}/turismo/cruce/agencia/${agenciaId}`, { headers: getAuthHeaders() });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setErrorMsg(body.detail || `Error ${res.status} cargando el detalle`);
          setData(null);
        } else {
          setData(await res.json());
        }
      } catch (e) {
        setErrorMsg('Error de red cargando el detalle del cruce');
      }
      setLoading(false);
    })();
  }, [agenciaId]);

  const totales = data?.totales || {};

  // Unificar reservas: la misma reserva suele tener AMBAS cosas (deuda y
  // comisión). Mostrarlas en filas separadas duplica info y obliga al operador
  // a cruzar mentalmente. Acá las combinamos por id, ordenadas FIFO por fecha.
  const reservasUnificadas = React.useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const r of (data.reservas_deuda || [])) {
      map.set(r.id, {
        id: r.id, codigo: r.codigo, cliente: r.cliente, fecha: r.fecha,
        paquete: r.paquete, precio_total: r.precio_total || 0,
        saldo_turista: r.saldo_turista || 0,
        voucher_aprobado: r.voucher_aprobado || 0,
        pagos_previos: r.pagos_previos || 0,
        deuda: r.deuda || 0,
        comision: 0, comision_porcentaje: 0,
      });
    }
    for (const c of (data.comisiones_pendientes || [])) {
      const existing = map.get(c.id);
      if (existing) {
        existing.comision = c.comision || 0;
        existing.comision_porcentaje = c.comision_porcentaje || 0;
      } else {
        map.set(c.id, {
          id: c.id, codigo: c.codigo, cliente: c.cliente, fecha: c.fecha,
          paquete: c.paquete, precio_total: 0, saldo_turista: 0,
          voucher_aprobado: 0, pagos_previos: 0, deuda: 0,
          comision: c.comision || 0, comision_porcentaje: c.comision_porcentaje || 0,
        });
      }
    }
    return [...map.values()].sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''));
  }, [data]);

  // Auto-switch: hasta 5 reservas usamos vista de cards (cómoda, 1 por bloque);
  // a partir de 6 cambiamos a tabla unificada (compacta, escaneable).
  const usarCards = reservasUnificadas.length <= 5;

  return (
    <StyledDialog open onClose={onClose}
      title={`Cruce — ${data?.agencia?.nombre || '...'}`}
      icon={<CompareArrowsIcon />} variant="compact" maxWidth="md">
      <StyledDialog.Body compact>
        {loading ? <CircularProgress /> : errorMsg ? (
          <Alert severity="error" sx={{ fontSize: 12 }}>
            {errorMsg}
          </Alert>
        ) : !data ? (
          <Alert severity="warning" sx={{ fontSize: 12 }}>
            No se recibieron datos del cruce.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip label={`Deuda total: ${fmtMoney(totales.total_deuda)}`} size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700 }} />
              <Chip label={`Comisión pendiente: ${fmtMoney(totales.total_comision_pendiente)}`} size="small" sx={{ bgcolor: '#fefce8', color: '#a16207', fontWeight: 700 }} />
              <Chip label={`Cruce neto: ${fmtMoney(totales.neto)}`} size="small" sx={{ bgcolor: totales.neto > 0 ? '#fef2f2' : '#f0fdfa', color: totales.neto > 0 ? '#dc2626' : '#0d9488', fontWeight: 700 }} />
              <Chip label={`A compensar: ${fmtMoney(totales.monto_compensable)}`} size="small" sx={{ bgcolor: '#ecfdf5', color: '#0d9488', fontWeight: 700 }} />
            </Box>

            <Alert severity="info" sx={{ fontSize: 12, mb: 2 }}>
              El cruce aplica FIFO: las reservas más antiguas se pagan primero hasta cubrir <strong>{fmtMoney(totales.monto_compensable)}</strong>. El saldo residual queda como deuda o comisión pendiente.
            </Alert>

            <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: 13, mb: 1.5 }}>
              Reservas a cruzar ({reservasUnificadas.length}) — orden FIFO
            </Typography>

            {reservasUnificadas.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: 12 }}>
                Esta agencia no tiene reservas con deuda ni comisiones pendientes.
              </Alert>
            ) : usarCards ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {reservasUnificadas.map(r => <CruceCard key={r.id} r={r} />)}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{
                ...cardStyle,
                overflow: 'visible', overflowX: 'auto', overflowY: 'visible',
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Reserva</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Deuda</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Comisión</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Neto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservasUnificadas.map(r => {
                      const neto = r.deuda - r.comision;
                      const colorNeto = neto > 0 ? '#dc2626' : neto < 0 ? '#0d9488' : '#64748b';
                      const signo = neto > 0 ? '+' : '';
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ fontSize: 11, fontWeight: 600, fontFamily: 'monospace' }}>{r.codigo}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{r.cliente}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{r.fecha?.slice(0, 10)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: r.deuda > 0 ? '#dc2626' : '#cbd5e1' }}>
                            {r.deuda > 0 ? fmtMoney(r.deuda) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: r.comision > 0 ? '#a16207' : '#cbd5e1' }}>
                            {r.comision > 0 ? fmtMoney(r.comision) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 12, fontWeight: 800, color: colorNeto }}>
                            {signo}{fmtMoney(Math.abs(neto))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Fila de totales */}
                    <TableRow sx={{ bgcolor: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                      <TableCell colSpan={3} sx={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>
                        Totales
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 800, color: '#dc2626' }}>
                        {fmtMoney(totales.total_deuda)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 800, color: '#a16207' }}>
                        {fmtMoney(totales.total_comision_pendiente)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 800, color: totales.neto > 0 ? '#dc2626' : '#0d9488' }}>
                        {totales.neto > 0 ? '+' : ''}{fmtMoney(Math.abs(totales.neto))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={onClose}>Cerrar</StyledDialog.CancelButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


/**
 * CruceCard — Tarjeta visual de una reserva en el detalle del cruce.
 * Muestra deuda + comisión + neto de una manera más amigable que la tabla,
 * usada cuando hay ≤5 reservas. Pensada para escaneo rápido del operador.
 */
function CruceCard({ r }) {
  const neto = r.deuda - r.comision;
  const aFavorDeEntretur = neto > 0;
  const aFavorDeAgencia = neto < 0;
  const cruceExacto = Math.abs(neto) < 0.5;

  const netoColor = cruceExacto ? '#64748b' : (aFavorDeEntretur ? '#dc2626' : '#0d9488');
  const netoBg    = cruceExacto ? '#f1f5f9' : (aFavorDeEntretur ? '#fef2f2' : '#f0fdfa');
  const netoLabel = cruceExacto
    ? 'Cruce exacto'
    : aFavorDeEntretur
      ? 'Neto a favor de Entretur'
      : 'Neto a favor de la agencia';

  return (
    <Box sx={{
      p: 1.5, borderRadius: 2,
      bgcolor: '#fff',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Header: código + fecha */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0f172a', fontFamily: 'monospace' }}>
          {r.codigo}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          {r.fecha?.slice(0, 10)}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: '#475569', mb: 1 }}>
        {r.cliente}{r.paquete ? ` · ${r.paquete}` : ''}
      </Typography>

      {/* Renglones de deuda y comisión */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1, pl: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 11.5, color: r.deuda > 0 ? '#475569' : '#cbd5e1' }}>
            🔴 Deuda turista pendiente
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: r.deuda > 0 ? '#dc2626' : '#cbd5e1' }}>
            {r.deuda > 0 ? fmtMoney(r.deuda) : '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 11.5, color: r.comision > 0 ? '#475569' : '#cbd5e1' }}>
            🟡 Comisión agencia pendiente{r.comision_porcentaje > 0 ? ` (${r.comision_porcentaje}%)` : ''}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: r.comision > 0 ? '#a16207' : '#cbd5e1' }}>
            {r.comision > 0 ? fmtMoney(r.comision) : '—'}
          </Typography>
        </Box>
      </Box>

      {/* Neto destacado */}
      <Box sx={{
        p: 1, borderRadius: 1.5, bgcolor: netoBg,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        border: `1px solid ${netoColor}30`,
      }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: netoColor }}>
          ⚖ {netoLabel}
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: netoColor }}>
          {fmtMoney(Math.abs(neto))}
        </Typography>
      </Box>

      {/* Detalle secundario (saldo turista, voucher, pagos previos) — solo si hay info útil */}
      {(r.saldo_turista > 0 || r.voucher_aprobado > 0 || r.pagos_previos > 0) && (
        <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.75, fontStyle: 'italic' }}>
          {r.saldo_turista > 0 && `Saldo turista ${fmtMoney(r.saldo_turista)} · `}
          {r.voucher_aprobado > 0 && `Voucher ${fmtMoney(r.voucher_aprobado)} · `}
          {r.pagos_previos > 0 && `Pago cruce previo ${fmtMoney(r.pagos_previos)}`}
        </Typography>
      )}
    </Box>
  );
}


function ConfirmarCruceDialog({ fila, onClose, onApplied }) {
  const [notas, setNotas] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const empresaId = getEmpresaId();

  const aplicar = async () => {
    setLoading(true); setError(null);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/turismo/cruce/aplicar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          empresa_id: empresaId,
          agencia_id: fila.agencia_id,
          notas: notas || null,
          aplicado_por_user_id: user.id || null,
          aplicado_por_nombre: user.nombre || user.name || user.email || null,
          aplicado_por_email: user.email || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Error');
      onApplied();
    } catch (e) {
      setError(e.message || 'Error aplicando cruce');
    }
    setLoading(false);
  };

  return (
    <StyledDialog open onClose={onClose}
      title="Confirmar cruce"
      subtitle={`Compensación entre deuda y comisiones de ${fila.agencia_nombre}`}
      icon={<CompareArrowsIcon />} variant="compact" maxWidth="sm">
      <StyledDialog.Body compact>
        <Alert severity="warning" sx={{ fontSize: 12, mb: 2 }}>
          Esta acción es legal y queda registrada en auditoría. Puede revertirse desde el historial si fuese necesario.
        </Alert>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderRadius: 2 }}>
            <Typography sx={{ fontSize: 10, color: '#991b1b', fontWeight: 600, textTransform: 'uppercase' }}>Deuda agencia</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#dc2626' }}>{fmtMoney(fila.total_deuda)}</Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: '#fefce8', borderRadius: 2 }}>
            <Typography sx={{ fontSize: 10, color: '#854d0e', fontWeight: 600, textTransform: 'uppercase' }}>Comisión pendiente</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#a16207' }}>{fmtMoney(fila.total_comision_pendiente)}</Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: '#ecfdf5', borderRadius: 2, gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: 10, color: '#065f46', fontWeight: 600, textTransform: 'uppercase' }}>A compensar (FIFO)</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0d9488' }}>{fmtMoney(fila.monto_compensable)}</Typography>
            {fila.neto !== 0 && (
              <Typography sx={{ fontSize: 11, color: '#475569', mt: 0.5 }}>
                {fila.neto > 0
                  ? `Quedará deuda residual de ${fmtMoney(fila.neto)} (la agencia debe a la empresa).`
                  : `Quedará comisión pendiente de ${fmtMoney(Math.abs(fila.neto))} (la empresa debe a la agencia).`}
              </Typography>
            )}
          </Box>
        </Box>

        <TextField sx={inputStyles} fullWidth multiline rows={2} label="Notas (opcional)"
          placeholder="Razón del cruce, referencia interna, etc."
          value={notas} onChange={e => setNotas(e.target.value)} />

        {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: 12 }}>{error}</Alert>}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={onClose}>Cancelar</StyledDialog.CancelButton>
        <StyledDialog.PrimaryButton fullWidth loading={loading} onClick={aplicar}>
          Aplicar cruce
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


// ============================================================================
// TAB: VENTAS DIRECTAS
// Reservas creadas desde panel admin (sin agencia, sin chatbot).
// ============================================================================
export function VentasDirectasTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonth());
  const [fechaHasta, setFechaHasta] = useState(today());
  const [filtroFormaPago, setFiltroFormaPago] = useState('todas');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const empresaId = getEmpresaId();
      const params = new URLSearchParams({ empresa_id: empresaId, fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
      const res = await fetch(`${API_BASE}/turismo/ventas-directas/resumen?${params}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Error al cargar');
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading || !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const k = data.kpis;
  const reservasFiltradas = filtroFormaPago === 'todas'
    ? data.reservas
    : data.reservas.filter(r => r.forma_pago === filtroFormaPago);

  const formaPagoLabel = (fp) => ({
    efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta',
    bold_link: 'Link BOLD', epayco_link: 'Link ePayco',
    credito: 'Crédito', datafono: 'Datáfono', muelle: 'Muelle',
  })[fp] || (fp || '—');

  const voucherColor = (estado) => ({
    aprobado: { bg: '#10b98115', color: '#10b981' },
    pendiente: { bg: '#f59e0b15', color: '#f59e0b' },
    rechazado: { bg: '#ef444415', color: '#ef4444' },
  })[estado] || { bg: '#64748b15', color: '#64748b' };

  return (
    <Box>
      {/* Filtros */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" sx={inputStyles} type="date" label="Desde"
          value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <TextField size="small" sx={inputStyles} type="date" label="Hasta"
          value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <FormControl size="small" sx={{ ...inputStyles, minWidth: 180 }}>
          <InputLabel>Forma de pago</InputLabel>
          <Select label="Forma de pago" value={filtroFormaPago}
            onChange={e => setFiltroFormaPago(e.target.value)}>
            <MenuItem value="todas">Todas</MenuItem>
            <MenuItem value="efectivo">Efectivo</MenuItem>
            <MenuItem value="transferencia">Transferencia</MenuItem>
            <MenuItem value="tarjeta">Tarjeta crédito/débito</MenuItem>
            <MenuItem value="bold_link">Link BOLD</MenuItem>
            <MenuItem value="epayco_link">Link ePayco</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" onClick={cargar}>Actualizar</Button>
      </Box>

      {/* KPIs */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(6,1fr)' }, gap: 1.5, mb: 3 }}>
        <KpiCardSimple label="Reservas" value={k.total_reservas} color="#0d9488" />
        <KpiCardSimple label="Ingreso bruto" value={fmtMoney(k.ingreso_bruto)} color="#475569" />
        <KpiCardSimple label="Descuentos otorgados" value={fmtMoney(k.descuento_total)} color="#16a34a" />
        <KpiCardSimple label="Ingreso neto" value={fmtMoney(k.ingreso_neto)} color="#0d9488" big />
        <KpiCardSimple label="Cobrado" value={fmtMoney(k.cobrado)} color="#10b981" />
        <KpiCardSimple label="Saldo pendiente" value={fmtMoney(k.saldo_pendiente)} color="#f59e0b" />
      </Box>

      {/* Breakdown por forma de pago */}
      {data.por_forma_pago.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
            Por forma de pago
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.por_forma_pago.map(g => (
              <Box key={g.forma_pago} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 160 }}>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{formaPagoLabel(g.forma_pago)}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#134e4a' }}>{fmtMoney(g.neto)}</Typography>
                <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                  {g.reservas} reserva{g.reservas !== 1 ? 's' : ''}
                  {g.saldo_pendiente > 0 && ` · Pend ${fmtMoney(g.saldo_pendiente)}`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Breakdown por autorizador (descuentos) */}
      {data.por_autorizador.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
            Descuentos por autorizador
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.por_autorizador.map(g => (
              <Box key={g.autorizador} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fffbeb', border: '1px dashed #fbbf24', minWidth: 200 }}>
                <Typography sx={{ fontSize: 11, color: '#a16207' }}>{g.autorizador}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#78350f' }}>{fmtMoney(g.descuento_total)}</Typography>
                <Typography sx={{ fontSize: 10, color: '#a16207' }}>{g.reservas} reserva{g.reservas !== 1 ? 's' : ''} con descuento</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Tabla */}
      <TableContainer component={Paper} sx={cardStyle}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Tour / Fecha</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Pax</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Lista</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#16a34a' }}>Descuento</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Neto</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Saldo</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Forma pago</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Soporte</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Autorizó dcto.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reservasFiltradas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                  Sin ventas directas en este rango.
                </TableCell>
              </TableRow>
            ) : reservasFiltradas.map(r => {
              const vColor = r.voucher_estado ? voucherColor(r.voucher_estado) : null;
              return (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>{r.codigo_reserva}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.nombre_cliente}</TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#134e4a' }}>{r.paquete_nombre || '—'}</Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>{r.fecha_tour || '—'}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 12 }}>
                    {r.adultos}A {r.ninos > 0 && `· ${r.ninos}N`} {r.bebes > 0 && `· ${r.bebes}B`}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmtMoney(r.precio_lista)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: r.descuento_comercial > 0 ? '#16a34a' : '#94a3b8', fontWeight: r.descuento_comercial > 0 ? 700 : 400 }}>
                    {r.descuento_comercial > 0 ? `− ${fmtMoney(r.descuento_comercial)}` : '—'}
                    {r.descuento_por_persona > 0 && (
                      <Typography component="span" sx={{ display: 'block', fontSize: 9, color: '#94a3b8', fontWeight: 400 }}>
                        {fmtMoney(r.descuento_por_persona)}/pax
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>{fmtMoney(r.neto)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: r.saldo_pendiente > 0 ? '#f59e0b' : '#94a3b8' }}>
                    {r.saldo_pendiente > 0 ? fmtMoney(r.saldo_pendiente) : '—'}
                  </TableCell>
                  <TableCell><Chip size="small" label={formaPagoLabel(r.forma_pago)} sx={{ height: 18, fontSize: 10 }} /></TableCell>
                  <TableCell>
                    {vColor ? (
                      <Chip size="small" label={r.voucher_estado} sx={{ height: 18, fontSize: 10, bgcolor: vColor.bg, color: vColor.color, fontWeight: 700 }} />
                    ) : <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>—</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    {r.autorizado_por ? (
                      <Tooltip title={r.motivo_descuento || ''}>
                        <span>{r.autorizado_por}</span>
                      </Tooltip>
                    ) : <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>—</Typography>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function KpiCardSimple({ label, value, color, big }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', textAlign: 'center' }}>
      <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: big ? 18 : 15, fontWeight: 700, color }}>{value}</Typography>
    </Box>
  );
}

/**
 * TileComision — calcado del Tile de VentasDiaPage.jsx: fondo tintado, rótulo
 * diminuto en mayúsculas y valor en monoespaciada compacta. El tooltip
 * devuelve la cifra exacta, que al compactar se pierde.
 */
function TileComision({ label, v, color, bg }) {
  const valor = Number(v || 0);
  // Valor completo, no compactado: en esta pantalla hay ancho de sobra y
  // "$5.670.000" se lee de una vez, mientras "$5,67M" obliga a traducir.
  const exacto = `$${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(valor)}`;

  const contenido = (
    <Box sx={{
      p: { xs: 1, md: 1.25 }, bgcolor: bg, borderRadius: 1.5,
      height: '100%', display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between', minWidth: 0, overflow: 'hidden', gap: 0.5,
    }}>
      <Typography sx={{
        fontSize: { xs: 9, sm: 9, lg: 8.5 },
        color, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.3, lineHeight: 1.2, alignSelf: 'flex-start',
        display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: { xs: 14, sm: 15, md: 15, lg: 16, xl: 17 },
        fontWeight: 800, color,
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums',
      }}>
        {exacto}
      </Typography>
    </Box>
  );

  return contenido;
}
